import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import ClientPicker from "../../../../Components/Studio/ClientPicker";
import ClientAnamnesisOffcanvas from "../../../../Components/Studio/ClientAnamnesisOffcanvas";
import {
  getHelpArticleUrl,
  HELP_ARTICLE_ORDERS,
  HELP_ARTICLE_ORDERS_SPLIT_VOID,
} from "../../../../helpers/helpCenter";
import { resolveCurrentTenantId } from "../../../../helpers/agendaUnitContext";
import {
  getOrdersLastOptionsActionForTenant,
  setOrdersLastOptionsActionForTenant,
  type OrdersOptionsActionPreference,
} from "../../../../helpers/ordersListContext";
import { canManageClinicalRecords } from "../../../../helpers/clinicalRecordAccess";
import {
  CatalogCategoryFilterValue,
  matchesCatalogCategoryFilter,
} from "../../../../helpers/catalogCategoryFilter";
import MoneyInput from "../../../../Components/Common/MoneyInput";
import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import PixPaymentModal from "../../../../Components/Studio/PixPaymentModal";
import TerminalPaymentModal from "../../../../Components/Studio/TerminalPaymentModal";
import OrdersFiltersPanel from "./OrdersFiltersPanel";
import OrdersOptionsMenu from "./OrdersOptionsMenu";
import {
  filterCheckoutMethodsByEffective,
  getMethodsWithLegacyFallback,
} from "../../../../services/paymentMethodSettingsService";
import {
  addItem,
  AddOrderItemPayload,
  checkout,
  CheckoutConsumptionItem,
  CheckoutPaymentLine,
  create,
  CreateOrderPayload,
  getAll,
  getById,
  ORDER_PAYMENT_METHOD_OPTIONS,
  Order,
  OrderItem,
  OrderPaymentMethod,
  removeDiscount,
  removeItem,
  updateItem,
  voidOrder,
  getVoidPreview,
} from "../../../../services/orderService";
import {
  getOpenExpiringInventory,
  getProducts,
  Product,
} from "../../../../services/productService";
import { getCatalogCategories } from "../../../../services/catalogCategoryService";
import {
  getProfessionals,
  Professional,
} from "../../../../services/professionalService";
import {
  getServiceProfessionals,
  ServiceProfessionalsData,
} from "../../../../services/professionalServiceEnablementService";
import { getServices, Service } from "../../../../services/serviceService";
import {
  getClientCredits,
  getClientLoyalty,
  getServicePackages,
  ServicePackage,
} from "../../../../services/servicePackageService";
import { getClientClubBenefitPreview } from "../../../../services/clientSubscriptionService";
import {
  DEFAULT_TENANT_SETTINGS,
  getSettings,
  TenantSettings,
} from "../../../../services/settingsService";
import { ClientLoyaltyProgress } from "../../../../types/servicePackage";
import { PaginationMeta } from "../../../../types/api";
import { CatalogCategory } from "../../../../types/catalogCategory";
import { getUnits, Unit } from "../../../../services/unitService";
import { getUsers, User as StudioUser } from "../../../../services/userService";
import { userHasAnyRole } from "../../../../services/authService";
import CheckoutConsumptionModal, {
  ConsumptionLine,
} from "./CheckoutConsumptionModal";
import { setLastOrderPaymentMethod } from "../../../../helpers/orderPaymentStorage";
import {
  clearPendingPackageSale,
  getPendingPackageSale,
  setPendingPackageSale,
} from "../../../../helpers/pendingPackageSaleStorage";
import CheckoutPaymentModal from "./CheckoutPaymentModal";
import ApplyDiscountModal from "./ApplyDiscountModal";
import OrderCommissionDetailsCard from "./OrderCommissionDetailsCard";
import OrderCommissionRateChangedBanner from "./OrderCommissionRateChangedBanner";
import OrderPaymentDetailsCard from "./OrderPaymentDetailsCard";

const PER_PAGE = 15;
const CATALOG_PAGE_SIZE = 100;
const BRAND_FILTER_DEBOUNCE_MS = 400;
const PENDING_PIX_ORDERS_STORAGE_KEY = "adatis.orders.pendingPixOrderIds";

const readPendingPixOrderIds = (): Set<number> => {
  try {
    const raw = sessionStorage.getItem(PENDING_PIX_ORDERS_STORAGE_KEY);

    if (!raw) {
      return new Set();
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!Array.isArray(parsed)) {
      return new Set();
    }

    return new Set(parsed.filter((value): value is number => typeof value === "number"));
  } catch {
    return new Set();
  }
};

const writePendingPixOrderIds = (ids: Set<number>): void => {
  sessionStorage.setItem(PENDING_PIX_ORDERS_STORAGE_KEY, JSON.stringify(Array.from(ids)));
};

const orderHasPixPayload = (order: Order): boolean =>
  Boolean(order.pix?.qr_code_base64 || order.pix?.copy_paste_string);

type OrderStatusFilter = "open" | "paid" | "voided" | "all";
type PaymentMethodFilter = OrderPaymentMethod | "";
type OrderSortFilter = "id_desc" | "discount_desc" | "discount_asc";

const parseStatusFilterFromUrl = (value: string | null): OrderStatusFilter => {
  if (value === "paid" || value === "open" || value === "voided" || value === "all") {
    return value;
  }

  return "open";
};

const isOrderPaymentMethod = (value: string): value is OrderPaymentMethod =>
  ORDER_PAYMENT_METHOD_OPTIONS.includes(value as OrderPaymentMethod);

const parsePaymentMethodFilterFromUrl = (value: string | null): PaymentMethodFilter => {
  if (value && isOrderPaymentMethod(value)) {
    return value;
  }

  return "";
};

const parseSortFilterFromUrl = (value: string | null): OrderSortFilter => {
  if (value === "discount_desc" || value === "discount_asc" || value === "id_desc") {
    return value;
  }

  return "id_desc";
};

const escapeCsvCell = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

type NewOrderFormValues = {
  unit_id: string;
  client_id: string;
};

type AddItemFormValues = {
  item_type: "service" | "retail" | "package";
  service_id: string;
  product_id: string;
  service_package_id: string;
  professional_id: string;
  quantity: number;
  execution_notes: string;
  use_credit: boolean;
};

const emptyNewOrderValues = (): NewOrderFormValues => ({
  unit_id: "",
  client_id: "",
});

const emptyAddItemValues = (): AddItemFormValues => ({
  item_type: "service",
  service_id: "",
  product_id: "",
  service_package_id: "",
  professional_id: "",
  quantity: 1,
  execution_notes: "",
  use_credit: true,
});

const OrdersList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { orderId: orderIdParam } = useParams<{ orderId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedOrderIdRef = useRef<number | null>(null);
  const deepLinkNewOrderRef = useRef<string | null>(null);
  const pendingPackageAppliedRef = useRef<number | null>(null);
  const [pendingPackageBannerId, setPendingPackageBannerId] = useState<number | null>(null);

  document.title = t("studio.orders.pageTitle");

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const formatDateTime = useCallback(
    (value: string) =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value)),
    [i18n.language]
  );

  const formatRelativeTime = useCallback(
    (value: string): string => {
      const target = new Date(value).getTime();
      const now = Date.now();
      const diffSeconds = Math.round((target - now) / 1000);
      const absSeconds = Math.abs(diffSeconds);
      const formatter = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });

      if (absSeconds < 60) {
        return formatter.format(diffSeconds, "second");
      }

      const diffMinutes = Math.round(diffSeconds / 60);

      if (Math.abs(diffMinutes) < 60) {
        return formatter.format(diffMinutes, "minute");
      }

      const diffHours = Math.round(diffMinutes / 60);

      if (Math.abs(diffHours) < 24) {
        return formatter.format(diffHours, "hour");
      }

      const diffDays = Math.round(diffHours / 24);

      if (Math.abs(diffDays) < 30) {
        return formatter.format(diffDays, "day");
      }

      const diffMonths = Math.round(diffDays / 30);

      if (Math.abs(diffMonths) < 12) {
        return formatter.format(diffMonths, "month");
      }

      return formatter.format(Math.round(diffMonths / 12), "year");
    },
    [i18n.language]
  );

  const formatOldestDate = useCallback(
    (value: string) =>
      new Intl.DateTimeFormat(i18n.language, {
        day: "numeric",
        month: "long",
      }).format(new Date(value)),
    [i18n.language]
  );

  const [orders, setOrders] = useState<Order[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>(() =>
    parseStatusFilterFromUrl(searchParams.get("status"))
  );
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<PaymentMethodFilter>(() =>
    parsePaymentMethodFilterFromUrl(searchParams.get("payment_method"))
  );
  const [hasDiscountFilter, setHasDiscountFilter] = useState(
    () => searchParams.get("has_discount") === "1"
  );
  const [appliedByUserId, setAppliedByUserId] = useState<number | "">(() => {
    const raw = searchParams.get("applied_by_user_id");
    if (!raw) {
      return "";
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : "";
  });
  const [teamUsers, setTeamUsers] = useState<StudioUser[]>([]);
  const canFilterByApplier = userHasAnyRole(["owner", "manager"]);
  const [sortFilter, setSortFilter] = useState<OrderSortFilter>(() =>
    parseSortFilterFromUrl(searchParams.get("sort"))
  );
  const [exportingCsv, setExportingCsv] = useState(false);
  const [lastOptionsAction, setLastOptionsAction] =
    useState<OrdersOptionsActionPreference | null>(() =>
      getOrdersLastOptionsActionForTenant(resolveCurrentTenantId())
    );
  const [paymentMethodFilterOptions, setPaymentMethodFilterOptions] = useState<
    OrderPaymentMethod[]
  >(ORDER_PAYMENT_METHOD_OPTIONS);
  const [loadingPaymentMethodFilters, setLoadingPaymentMethodFilters] = useState(true);

  const [units, setUnits] = useState<Unit[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [servicePackages, setServicePackages] = useState<ServicePackage[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);

  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [consumptionPromptOpen, setConsumptionPromptOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [pendingCheckoutConsumptions, setPendingCheckoutConsumptions] = useState<
    CheckoutConsumptionItem[]
  >([]);
  const [pixModalOpen, setPixModalOpen] = useState(false);
  const [pixOrderId, setPixOrderId] = useState<number | null>(null);
  const [pixQrCodeBase64, setPixQrCodeBase64] = useState("");
  const [pixCopyPasteString, setPixCopyPasteString] = useState("");
  const [terminalModalOpen, setTerminalModalOpen] = useState(false);
  const [terminalOrderId, setTerminalOrderId] = useState<number | null>(null);
  const [terminalCardType, setTerminalCardType] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [anamnesisOpen, setAnamnesisOpen] = useState(false);
  const [anamnesisServiceId, setAnamnesisServiceId] = useState<number | null>(null);

  const [internalProducts, setInternalProducts] = useState<Product[]>([]);
  const [loadingInternalProducts, setLoadingInternalProducts] = useState(false);
  const [consumptionLines, setConsumptionLines] = useState<ConsumptionLine[]>([]);
  const [draftProductId, setDraftProductId] = useState("");
  const [draftQuantity, setDraftQuantity] = useState(1);
  const [consumptionDraftError, setConsumptionDraftError] = useState<string | null>(null);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings>(DEFAULT_TENANT_SETTINGS);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [openOrderExistsAlert, setOpenOrderExistsAlert] = useState<{
    message: string;
    orderId: number;
    withPackage?: boolean;
  } | null>(null);
  const [editingItem, setEditingItem] = useState<OrderItem | null>(null);
  const [editNotesValue, setEditNotesValue] = useState("");
  /** Inline unit_price edit in the items table (ADJ-F2-136) — not the notes modal. */
  const [inlinePriceItemId, setInlinePriceItemId] = useState<number | null>(null);
  const [inlinePriceDisplay, setInlinePriceDisplay] = useState("");
  const [catalogCategories, setCatalogCategories] = useState<CatalogCategory[]>([]);
  const [addItemCategoryFilter, setAddItemCategoryFilter] =
    useState<CatalogCategoryFilterValue>("all");
  const [addItemBrandFilter, setAddItemBrandFilter] = useState("");
  const [debouncedAddItemBrandFilter, setDebouncedAddItemBrandFilter] = useState("");
  const [serviceEnablement, setServiceEnablement] = useState<ServiceProfessionalsData | null>(
    null
  );
  const [loadingServiceEnablement, setLoadingServiceEnablement] = useState(false);
  const [serviceCreditSessions, setServiceCreditSessions] = useState<number | null>(null);
  const [protocolCreditHint, setProtocolCreditHint] = useState<{
    protocol_name: string;
    session_number: number;
  } | null>(null);
  const [serviceLoyaltyFree, setServiceLoyaltyFree] = useState(0);
  const [serviceLoyaltyRemaining, setServiceLoyaltyRemaining] = useState<number | null>(null);
  const [serviceLoyaltyValidityDays, setServiceLoyaltyValidityDays] = useState<number | null>(null);
  const [serviceLoyaltyExpiresLabel, setServiceLoyaltyExpiresLabel] = useState<string | null>(null);
  const [clubBenefitPreview, setClubBenefitPreview] = useState<{
    covered: boolean;
    limit_reached: boolean;
    plan_name: string | null;
    visits_used: number | null;
    visits_limit: number | null;
  } | null>(null);
  const [clientLoyaltyCache, setClientLoyaltyCache] = useState<ClientLoyaltyProgress[]>([]);
  const [loadingServiceCredits, setLoadingServiceCredits] = useState(false);
  const [pendingPixOrderIds, setPendingPixOrderIds] = useState<Set<number>>(() =>
    readPendingPixOrderIds()
  );
  const debouncedAddItemBrandFilterRef = useRef(debouncedAddItemBrandFilter);

  useEffect(() => {
    debouncedAddItemBrandFilterRef.current = debouncedAddItemBrandFilter;
  }, [debouncedAddItemBrandFilter]);

  const markOrderPixPending = useCallback((orderId: number) => {
    setPendingPixOrderIds((previous) => {
      const next = new Set(previous);
      next.add(orderId);
      writePendingPixOrderIds(next);
      return next;
    });
  }, []);

  const clearOrderPixPending = useCallback((orderId: number) => {
    setPendingPixOrderIds((previous) => {
      if (!previous.has(orderId)) {
        return previous;
      }

      const next = new Set(previous);
      next.delete(orderId);
      writePendingPixOrderIds(next);
      return next;
    });
  }, []);

  const orderHasPendingPix = useCallback(
    (order: Order): boolean =>
      order.status === "open"
      && (orderHasPixPayload(order) || pendingPixOrderIds.has(order.id)),
    [pendingPixOrderIds]
  );

  const notAvailable = t("studio.orders.common.notAvailable");
  const useCatalogCategories = tenantSettings.use_catalog_categories;
  const restrictProfessionalServices = tenantSettings.restrict_professional_services;
  const useCommissionTracking = tenantSettings.use_commission_tracking;

  const visiblePaymentMethodFilterOptions = useMemo(() => {
    const options = new Set(paymentMethodFilterOptions);

    if (paymentMethodFilter && isOrderPaymentMethod(paymentMethodFilter)) {
      options.add(paymentMethodFilter);
    }

    return ORDER_PAYMENT_METHOD_OPTIONS.filter((method) => options.has(method));
  }, [paymentMethodFilter, paymentMethodFilterOptions]);

  const handleStatusFilterChange = (value: OrderStatusFilter) => {
    setStatusFilter(value);
  };

  const handlePaymentMethodFilterChange = (value: PaymentMethodFilter) => {
    setPaymentMethodFilter(value);

    if (value && statusFilter === "open") {
      setStatusFilter("paid");
    }
  };

  const handleHasDiscountFilterChange = (checked: boolean) => {
    setHasDiscountFilter(checked);

    if (!checked) {
      setAppliedByUserId("");
    }
  };

  const handleSortFilterChange = (value: OrderSortFilter) => {
    setSortFilter(value);
  };

  const handleClearAdvancedFilters = () => {
    setPaymentMethodFilter("");
    setHasDiscountFilter(false);
    setAppliedByUserId("");
    setSortFilter("id_desc");
  };

  const handleRememberOptionsAction = useCallback(
    (action: OrdersOptionsActionPreference) => {
      setLastOptionsAction(action);
      setOrdersLastOptionsActionForTenant(resolveCurrentTenantId(), action);
    },
    []
  );

  const filteredServices = useMemo(() => {
    if (!useCatalogCategories) {
      return services;
    }

    return services.filter((service) =>
      matchesCatalogCategoryFilter(service.category_id, addItemCategoryFilter)
    );
  }, [services, useCatalogCategories, addItemCategoryFilter]);

  const filteredProducts = useMemo(() => {
    if (!useCatalogCategories) {
      return products;
    }

    return products.filter((product) =>
      matchesCatalogCategoryFilter(product.category_id, addItemCategoryFilter)
    );
  }, [products, useCatalogCategories, addItemCategoryFilter]);

  const newOrderSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(t("studio.orders.validation.unitRequired")),
        client_id: Yup.string().required(t("studio.orders.validation.clientRequired")),
      }),
    [t]
  );

  const addItemSchema = useMemo(
    () =>
      Yup.object({
        item_type: Yup.string()
          .oneOf(["service", "retail", "package"])
          .required(t("studio.orders.validation.itemTypeRequired")),
        service_id: Yup.string().when("item_type", {
          is: "service",
          then: (schema) => schema.required(t("studio.orders.validation.serviceRequired")),
          otherwise: (schema) => schema.notRequired(),
        }),
        product_id: Yup.string().when("item_type", {
          is: "retail",
          then: (schema) => schema.required(t("studio.orders.validation.productRequired")),
          otherwise: (schema) => schema.notRequired(),
        }),
        service_package_id: Yup.string().when("item_type", {
          is: "package",
          then: (schema) =>
            schema.required(t("studio.orders.validation.packageRequired")),
          otherwise: (schema) => schema.notRequired(),
        }),
        quantity: Yup.number()
          .typeError(t("studio.orders.validation.quantityMin"))
          .min(1, t("studio.orders.validation.quantityMin"))
          .required(t("studio.orders.validation.quantityMin")),
        execution_notes: Yup.string().when("item_type", {
          is: "service",
          then: (schema) => schema.max(2000, t("orders.items.validation.executionNotesMax")),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [t]
  );

  const loadOrders = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getAll({
        page,
        per_page: PER_PAGE,
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(paymentMethodFilter ? { payment_method: paymentMethodFilter } : {}),
        ...(hasDiscountFilter ? { has_discount: 1 } : {}),
        ...(appliedByUserId !== "" ? { applied_by_user_id: appliedByUserId } : {}),
        ...(sortFilter !== "id_desc" ? { sort: sortFilter } : {}),
      });
      setOrders(response.data);
      setMeta(response.meta);

      setPendingPixOrderIds((previous) => {
        const paidOrderIds = new Set(
          response.data.filter((order) => order.status === "paid").map((order) => order.id)
        );
        let changed = false;
        const next = new Set<number>();

        previous.forEach((orderId) => {
          if (paidOrderIds.has(orderId)) {
            changed = true;
          } else {
            next.add(orderId);
          }
        });

        if (!changed) {
          return previous;
        }

        writePendingPixOrderIds(next);
        return next;
      });
    } catch (error: any) {
      toast.error(error?.data || t("studio.orders.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [
    appliedByUserId,
    hasDiscountFilter,
    page,
    paymentMethodFilter,
    sortFilter,
    statusFilter,
    t,
  ]);

  const buildListQueryParams = useCallback(
    (pageNumber: number, perPage: number) => ({
      page: pageNumber,
      per_page: perPage,
      ...(statusFilter !== "all" ? { status: statusFilter } : {}),
      ...(paymentMethodFilter ? { payment_method: paymentMethodFilter } : {}),
      ...(hasDiscountFilter ? { has_discount: 1 as const } : {}),
      ...(appliedByUserId !== "" ? { applied_by_user_id: appliedByUserId } : {}),
      ...(sortFilter !== "id_desc" ? { sort: sortFilter } : {}),
    }),
    [
      appliedByUserId,
      hasDiscountFilter,
      paymentMethodFilter,
      sortFilter,
      statusFilter,
    ]
  );

  const handleExportCsv = useCallback(async () => {
    setExportingCsv(true);

    try {
      const rows: Order[] = [];
      const maxPages = 10;
      let pageNumber = 1;
      let lastPage = 1;

      do {
        const response = await getAll(buildListQueryParams(pageNumber, 100));
        rows.push(...response.data);
        lastPage = Math.min(response.meta.last_page ?? 1, maxPages);
        pageNumber += 1;
      } while (pageNumber <= lastPage);

      const header = [
        t("studio.orders.table.id"),
        t("studio.orders.table.client"),
        t("studio.orders.discount.subtotal"),
        t("studio.orders.discount.applied"),
        t("studio.orders.table.total"),
        t("studio.orders.table.status"),
        t("studio.orders.discount.reasonShown"),
        t("studio.orders.discount.appliedByHeader"),
        t("studio.orders.table.createdAt"),
      ].join(",");

      const statusLabel = (status: string): string => {
        if (status === "paid") {
          return t("studio.orders.status.paid");
        }
        if (status === "voided") {
          return t("studio.orders.status.voided");
        }
        return t("studio.orders.status.open");
      };

      const body = rows
        .map((order) => {
          const discount = order.discounts?.[0];

          return [
            String(order.id),
            escapeCsvCell(order.client?.name ?? ""),
            escapeCsvCell(
              formatMoney(order.subtotal_amount ?? order.total_amount)
            ),
            escapeCsvCell(formatMoney(order.discount_total_cents ?? 0)),
            escapeCsvCell(formatMoney(order.total_amount)),
            escapeCsvCell(statusLabel(order.status)),
            escapeCsvCell(discount?.reason ?? ""),
            escapeCsvCell(discount?.applied_by?.name ?? ""),
            escapeCsvCell(formatDateTime(order.created_at)),
          ].join(",");
        })
        .join("\n");

      const blob = new Blob([`${header}\n${body}`], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `comandas-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(
        t("studio.orders.toast.exportCsvSuccess", { count: rows.length })
      );
    } catch (error: any) {
      toast.error(error?.data || t("studio.orders.toast.exportCsvError"));
    } finally {
      setExportingCsv(false);
    }
  }, [buildListQueryParams, formatDateTime, formatMoney, t]);

  const loadFormOptions = useCallback(async () => {
    try {
      const [unitsResponse, professionalsResponse] = await Promise.all([
        getUnits({ per_page: CATALOG_PAGE_SIZE }),
        getProfessionals({ per_page: CATALOG_PAGE_SIZE }),
      ]);

      setUnits(unitsResponse.data);
      setProfessionals(professionalsResponse.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.orders.toast.loadError"));
    }
  }, [t]);

  const loadCatalogForUnit = useCallback(
    async (unitId: number, retailBrand?: string) => {
      try {
        const [servicesResponse, productsResponse, packagesResponse] =
          await Promise.all([
            getServices({ per_page: CATALOG_PAGE_SIZE, unit_id: unitId }),
            getProducts({
              per_page: CATALOG_PAGE_SIZE,
              unit_id: unitId,
              product_type: "retail",
              ...(retailBrand ? { brand: retailBrand } : {}),
            }),
            getServicePackages({
              per_page: CATALOG_PAGE_SIZE,
              unit_id: unitId,
              is_active: true,
            }),
          ]);

        setServices(servicesResponse.data);
        setProducts(productsResponse.data);
        setServicePackages(packagesResponse.data);
      } catch (error: any) {
        toast.error(error?.data || t("studio.orders.toast.loadError"));
      }
    },
    [t]
  );

  const reloadRetailProductsForUnit = useCallback(
    async (unitId: number, retailBrand?: string) => {
      try {
        const response = await getProducts({
          per_page: CATALOG_PAGE_SIZE,
          unit_id: unitId,
          product_type: "retail",
          ...(retailBrand ? { brand: retailBrand } : {}),
        });
        setProducts(response.data);
      } catch (error: any) {
        toast.error(error?.data || t("studio.orders.toast.loadError"));
      }
    },
    [t]
  );

  const loadInternalProductsForUnit = useCallback(
    async (unitId: number) => {
      setLoadingInternalProducts(true);

      try {
        const response = await getProducts({
          per_page: CATALOG_PAGE_SIZE,
          unit_id: unitId,
          product_type: "internal",
        });
        setInternalProducts(response.data);
      } catch (error: any) {
        toast.error(error?.data || t("studio.orders.toast.loadError"));
      } finally {
        setLoadingInternalProducts(false);
      }
    },
    [t]
  );

  const loadTenantSettings = useCallback(async () => {
    setLoadingSettings(true);

    try {
      const response = await getSettings();
      setTenantSettings(response.data);

      if (response.data.use_catalog_categories) {
        const categoriesResponse = await getCatalogCategories({
          per_page: CATALOG_PAGE_SIZE,
          is_active: true,
        });
        setCatalogCategories(categoriesResponse.data);
      } else {
        setCatalogCategories([]);
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.orders.toast.loadError"));
      setTenantSettings(DEFAULT_TENANT_SETTINGS);
    } finally {
      setLoadingSettings(false);
    }
  }, [t]);

  const maybeWarnOpenShelfUseToday = useCallback(
    async (unitId: number) => {
      const dayKey = new Date().toISOString().slice(0, 10);
      const storageKey = `adatis.open_shelf_use_today.${unitId}.${dayKey}`;
      try {
        if (sessionStorage.getItem(storageKey)) {
          return;
        }
      } catch {
        // sessionStorage may be unavailable
      }

      try {
        const response = await getOpenExpiringInventory({
          unit_id: unitId,
          within_days: 0,
        });
        const count = response.data?.length ?? 0;
        if (count <= 0) {
          return;
        }
        try {
          sessionStorage.setItem(storageKey, "1");
        } catch {
          // ignore
        }
        toast.warning(
          t("studio.orders.toast.openShelfUseTodayWarning", { count })
        );
      } catch {
        // Soft reminder — never block the comanda
      }
    },
    [t]
  );

  const refreshOrderDetail = useCallback(
    async (orderId: number) => {
      setDetailLoading(true);

      try {
        const response = await getById(orderId);
        setSelectedOrder(response.data);

        if (response.data.status === "paid") {
          clearOrderPixPending(orderId);
        }

        await loadCatalogForUnit(
          response.data.unit_id,
          debouncedAddItemBrandFilterRef.current || undefined
        );
        void maybeWarnOpenShelfUseToday(response.data.unit_id);
      } catch (error: any) {
        toast.error(error?.data || t("studio.orders.toast.detailError"));
      } finally {
        setDetailLoading(false);
      }
    },
    [clearOrderPixPending, loadCatalogForUnit, maybeWarnOpenShelfUseToday, t]
  );

  useEffect(() => {
    setPage(1);
  }, [
    appliedByUserId,
    hasDiscountFilter,
    paymentMethodFilter,
    sortFilter,
    statusFilter,
  ]);

  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);

      if (statusFilter === "open") {
        next.delete("status");
      } else {
        next.set("status", statusFilter);
      }

      if (paymentMethodFilter) {
        next.set("payment_method", paymentMethodFilter);
      } else {
        next.delete("payment_method");
      }

      if (hasDiscountFilter) {
        next.set("has_discount", "1");
      } else {
        next.delete("has_discount");
      }

      if (appliedByUserId !== "") {
        next.set("applied_by_user_id", String(appliedByUserId));
      } else {
        next.delete("applied_by_user_id");
      }

      if (sortFilter === "id_desc") {
        next.delete("sort");
      } else {
        next.set("sort", sortFilter);
      }

      return next.toString() === current.toString() ? current : next;
    }, { replace: true });
  }, [
    appliedByUserId,
    hasDiscountFilter,
    paymentMethodFilter,
    setSearchParams,
    sortFilter,
    statusFilter,
  ]);

  useEffect(() => {
    if (!canFilterByApplier) {
      return;
    }

    let cancelled = false;

    void getUsers({ per_page: 100 })
      .then((response) => {
        if (!cancelled) {
          setTeamUsers(response.data ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTeamUsers([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canFilterByApplier]);

  useEffect(() => {
    let cancelled = false;

    const loadPaymentMethodFilters = async () => {
      setLoadingPaymentMethodFilters(true);

      try {
        const response = await getMethodsWithLegacyFallback();
        const methods = filterCheckoutMethodsByEffective(response.data.effective.checkout);

        if (!cancelled) {
          setPaymentMethodFilterOptions(
            methods.length > 0 ? methods : ORDER_PAYMENT_METHOD_OPTIONS
          );
        }
      } catch {
        if (!cancelled) {
          setPaymentMethodFilterOptions(ORDER_PAYMENT_METHOD_OPTIONS);
        }
      } finally {
        if (!cancelled) {
          setLoadingPaymentMethodFilters(false);
        }
      }
    };

    void loadPaymentMethodFilters();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    loadFormOptions();
  }, [loadFormOptions]);

  useEffect(() => {
    loadTenantSettings();
  }, [loadTenantSettings]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedAddItemBrandFilter(addItemBrandFilter.trim()),
      BRAND_FILTER_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [addItemBrandFilter]);

  useEffect(() => {
    if (!detailOpen || !selectedOrder?.unit_id) {
      return;
    }

    void reloadRetailProductsForUnit(
      selectedOrder.unit_id,
      debouncedAddItemBrandFilter || undefined
    );
  }, [
    debouncedAddItemBrandFilter,
    detailOpen,
    reloadRetailProductsForUnit,
    selectedOrder?.unit_id,
  ]);

  useEffect(() => {
    if (!orderIdParam) {
      deepLinkedOrderIdRef.current = null;
      return;
    }

    const parsedOrderId = Number(orderIdParam);

    if (!Number.isFinite(parsedOrderId) || parsedOrderId <= 0) {
      return;
    }

    if (deepLinkedOrderIdRef.current === parsedOrderId) {
      return;
    }

    deepLinkedOrderIdRef.current = parsedOrderId;
    setDetailOpen(true);
    refreshOrderDetail(parsedOrderId);
  }, [orderIdParam, refreshOrderDetail]);

  const handleGoToOpenOrder = async (orderId: number) => {
    pendingPackageAppliedRef.current = null;
    setOpenOrderExistsAlert(null);
    setNewOrderOpen(false);
    deepLinkedOrderIdRef.current = orderId;
    navigate(`/orders/${orderId}`);
    setDetailOpen(true);
    await refreshOrderDetail(orderId);
  };

  const openDetail = async (order: Order) => {
    setDetailOpen(true);
    await refreshOrderDetail(order.id);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedOrder(null);
    setEditingItem(null);
    setEditNotesValue("");
    setInlinePriceItemId(null);
    setInlinePriceDisplay("");
    setAnamnesisOpen(false);
    setAnamnesisServiceId(null);
    setAddItemCategoryFilter("all");
    setAddItemBrandFilter("");
    setDebouncedAddItemBrandFilter("");
    addItemForm.resetForm({ values: emptyAddItemValues() });
  };

  const closeCheckoutPrompt = () => {
    setConsumptionPromptOpen(false);
    setConsumptionLines([]);
    setDraftProductId("");
    setDraftQuantity(1);
    setConsumptionDraftError(null);
    setInternalProducts([]);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPendingCheckoutConsumptions([]);
  };

  const closePixModal = () => {
    setPixModalOpen(false);
    setPixOrderId(null);
    setPixQrCodeBase64("");
    setPixCopyPasteString("");
  };

  const closeTerminalModal = () => {
    setTerminalModalOpen(false);
    setTerminalOrderId(null);
    setTerminalCardType(null);
  };

  const openPaymentModal = async (consumptions: CheckoutConsumptionItem[] = []) => {
    if (selectedOrder?.needs_health_record && canManageClinicalRecords()) {
      const healthPrompt = await Swal.fire({
        title: t("studio.orders.anamnesis.checkoutMissingTitle"),
        text: t("studio.orders.anamnesis.checkoutMissingText"),
        icon: "warning",
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: t("studio.orders.anamnesis.fillNow"),
        denyButtonText: t("studio.orders.anamnesis.continueAnyway"),
        cancelButtonText: t("studio.orders.actions.cancel"),
        confirmButtonColor: "#f7b84b",
        denyButtonColor: "#0ab39c",
        cancelButtonColor: "#878a99",
        focusCancel: true,
      });

      if (healthPrompt.isConfirmed) {
        setAnamnesisServiceId(selectedOrder.health_prompt_service_id ?? null);
        setAnamnesisOpen(true);
        return;
      }

      if (!healthPrompt.isDenied) {
        return;
      }
    }

    const retailWithoutProfessional =
      Boolean(tenantSettings.commission_on_products) &&
      useCommissionTracking &&
      (selectedOrder?.order_items ?? []).some(
        (item) =>
          item.item_type === "retail" &&
          (item.professional_id == null || item.professional_id === 0)
      );

    if (retailWithoutProfessional) {
      toast.warning(
        t("studio.orders.checkout.retailWithoutProfessionalWarning")
      );
    }

    const confirmation = await Swal.fire({
      title: t("studio.orders.checkout.confirmTitle"),
      text: useCommissionTracking
        ? retailWithoutProfessional
          ? t("studio.orders.checkout.retailWithoutProfessionalWarning")
          : t("studio.orders.checkout.confirmDescription")
        : t("studio.orders.checkout.confirmDescriptionNoCommission"),
      icon: retailWithoutProfessional ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: t("studio.orders.checkout.payment.confirmPayment"),
      cancelButtonText: t("studio.orders.actions.cancel"),
      confirmButtonColor: "#0ab39c",
      cancelButtonColor: "#878a99",
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setPendingCheckoutConsumptions(consumptions);
    closeCheckoutPrompt();
    setPaymentModalOpen(true);
  };

  const openCheckoutPrompt = async () => {
    if (!selectedOrder) {
      return;
    }

    setConsumptionLines([]);
    setDraftProductId("");
    setDraftQuantity(1);
    setConsumptionDraftError(null);
    setConsumptionPromptOpen(true);
    await loadInternalProductsForUnit(selectedOrder.unit_id);
  };

  const newOrderForm = useFormik<NewOrderFormValues>({
    initialValues: emptyNewOrderValues(),
    validationSchema: newOrderSchema,
    onSubmit: async (values, helpers) => {
      setSubmitting(true);

      const payload: CreateOrderPayload = {
        unit_id: Number(values.unit_id),
        client_id: Number(values.client_id),
      };

      try {
        const response = await create(payload);
        toast.success(response.message || t("studio.orders.toast.createSuccess"));
        setNewOrderOpen(false);
        helpers.resetForm({ values: emptyNewOrderValues() });
        await loadOrders();
        await openDetail(response.data);
      } catch (error: any) {
        if (
          error?.code === "CLIENT_OPEN_ORDER_EXISTS" &&
          typeof error?.orderId === "number"
        ) {
          const hasPendingPackage =
            tenantSettings.tenant_id > 0 &&
            getPendingPackageSale(tenantSettings.tenant_id) !== null;

          setOpenOrderExistsAlert({
            message: hasPendingPackage
              ? t("studio.orders.alerts.openExistsWithPackage")
              : error.data || t("studio.orders.toast.createError"),
            orderId: error.orderId,
            withPackage: hasPendingPackage,
          });
          return;
        }

        toast.error(error?.data || t("studio.orders.toast.createError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const clientIdParam = searchParams.get("client_id");
    const actionParam = searchParams.get("action");
    const packageIdParam = Number(searchParams.get("service_package_id"));
    const hasPackageDeepLink =
      Number.isFinite(packageIdParam) && packageIdParam > 0;

    if (tenantSettings.tenant_id > 0 && hasPackageDeepLink) {
      setPendingPackageSale(tenantSettings.tenant_id, {
        service_package_id: packageIdParam,
      });
      setPendingPackageBannerId(packageIdParam);
    } else if (tenantSettings.tenant_id > 0) {
      const pending = getPendingPackageSale(tenantSettings.tenant_id);
      setPendingPackageBannerId(pending?.service_package_id ?? null);
    }

    if (actionParam === "new" && !clientIdParam) {
      const linkKey = `action:new:${packageIdParam || 0}`;
      if (deepLinkNewOrderRef.current === linkKey) {
        return;
      }
      deepLinkNewOrderRef.current = linkKey;
      setNewOrderOpen(true);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete("action");
        next.delete("item_type");
        next.delete("service_package_id");
        return next;
      }, { replace: true });
      return;
    }

    if (!clientIdParam) {
      deepLinkNewOrderRef.current = null;
      return;
    }

    const parsedClientId = Number(clientIdParam);

    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      return;
    }

    if (actionParam && actionParam !== "new") {
      return;
    }

    const linkKey = `${parsedClientId}:${actionParam ?? "implicit"}:${packageIdParam || 0}`;

    if (deepLinkNewOrderRef.current === linkKey) {
      return;
    }

    deepLinkNewOrderRef.current = linkKey;

    const clearDeepLinkParams = () => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete("client_id");
        next.delete("action");
        next.delete("item_type");
        next.delete("service_package_id");
        return next;
      }, { replace: true });
    };

    let cancelled = false;

    const openDeepLink = async () => {
      if (hasPackageDeepLink) {
        try {
          const response = await getAll({
            client_id: parsedClientId,
            status: "open",
            per_page: 1,
          });
          const openOrder = response.data?.[0];
          if (!cancelled && openOrder?.id) {
            clearDeepLinkParams();
            toast.info(t("studio.orders.toast.openedExistingForPackage"));
            await handleGoToOpenOrder(openOrder.id);
            return;
          }
        } catch {
          // Fall through to new-order modal.
        }
      }

      if (cancelled) {
        return;
      }

      newOrderForm.setFieldValue("client_id", String(parsedClientId));
      setNewOrderOpen(true);
      clearDeepLinkParams();
    };

    void openDeepLink();

    return () => {
      cancelled = true;
    };
    // handleGoToOpenOrder is one-shot deep-link navigation; including it re-runs after every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newOrderForm, searchParams, setSearchParams, t, tenantSettings.tenant_id]);

  const addItemForm = useFormik<AddItemFormValues>({
    initialValues: emptyAddItemValues(),
    validationSchema: addItemSchema,
    onSubmit: async (values, helpers) => {
      if (!selectedOrder) {
        return;
      }

      setSubmitting(true);

      const payload: AddOrderItemPayload = {
        item_type: values.item_type,
        quantity: Number(values.quantity),
        service_id: values.item_type === "service" ? Number(values.service_id) : null,
        product_id: values.item_type === "retail" ? Number(values.product_id) : null,
        service_package_id:
          values.item_type === "package" ? Number(values.service_package_id) : null,
        professional_id: values.professional_id ? Number(values.professional_id) : null,
      };

      if (values.item_type === "service") {
        payload.use_credit = values.use_credit;
        if (values.execution_notes.trim()) {
          payload.execution_notes = values.execution_notes.trim();
        }
      }

      // ADJ-F3.5-004 — Hard block expired inventory if setting enabled
      if (
        tenantSettings.block_expired_inventory &&
        values.item_type === "retail" &&
        values.product_id
      ) {
        const product = products.find(
          (item) => String(item.id) === values.product_id
        );
        const isExpired =
          (product?.expiring_within_days != null && product.expiring_within_days < 0) ||
          (product?.open_expiring_within_days != null && product.open_expiring_within_days < 0);

        if (
          isExpired &&
          product?.sellable_quantity != null &&
          product.sellable_quantity < values.quantity
        ) {
          toast.error(t("studio.orders.toast.blockedExpiredStock"));
          setSubmitting(false);
          return;
        }
      }

      try {
        const response = await addItem(selectedOrder.id, payload);
        toast.success(response.message || t("studio.orders.toast.addItemSuccess"));
        if (response.data?.paid_via_subscription) {
          toast.info(t("studio.orders.toast.clubUsed"));
        } else if (response.data?.paid_via_loyalty) {
          toast.info(t("studio.orders.toast.loyaltyUsed"));
        } else if (response.data?.paid_via_credit) {
          toast.info(t("studio.orders.toast.creditUsed"));
        }
        if (values.item_type === "retail" && values.product_id) {
          const product = products.find(
            (item) => String(item.id) === values.product_id
          );
          if (
            product?.expiring_within_days != null &&
            product.expiring_within_days < 0
          ) {
            toast.warning(t("studio.orders.toast.expiredStockWarning"));
          } else if (
            product?.expiring_within_days != null &&
            product.expiring_within_days <= 7
          ) {
            toast.warning(
              t("studio.orders.toast.expiringStockWarning", {
                days: product.expiring_within_days,
              })
            );
          }
          if (
            product?.open_expiring_within_days != null &&
            product.open_expiring_within_days < 0
          ) {
            toast.warning(t("studio.orders.toast.openShelfPastUseByWarning"));
          } else if (
            product?.open_expiring_within_days != null &&
            product.open_expiring_within_days <= 0
          ) {
            toast.warning(t("studio.orders.toast.openShelfUseTodayItemWarning"));
          }
        }
        setAddItemCategoryFilter("all");
        setServiceCreditSessions(null);
        setServiceLoyaltyFree(0);
        setServiceLoyaltyRemaining(null);
        helpers.resetForm({ values: emptyAddItemValues() });
        await refreshOrderDetail(selectedOrder.id);
        await loadOrders();
      } catch (error: any) {
        toast.error(error?.data || t("studio.orders.toast.addItemError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!detailOpen || !selectedOrder || tenantSettings.tenant_id <= 0) {
      return;
    }

    if (pendingPackageAppliedRef.current === selectedOrder.id) {
      return;
    }

    const pending = getPendingPackageSale(tenantSettings.tenant_id);
    if (!pending) {
      return;
    }

    if (servicePackages.length === 0) {
      return;
    }

    const exists = servicePackages.some(
      (pkg) => pkg.id === pending.service_package_id
    );
    if (!exists) {
      clearPendingPackageSale(tenantSettings.tenant_id);
      return;
    }

    pendingPackageAppliedRef.current = selectedOrder.id;
    addItemForm.setFieldValue("item_type", "package");
    addItemForm.setFieldValue("service_id", "");
    addItemForm.setFieldValue("product_id", "");
    addItemForm.setFieldValue(
      "service_package_id",
      String(pending.service_package_id)
    );
    clearPendingPackageSale(tenantSettings.tenant_id);
    setPendingPackageBannerId(null);
  }, [
    addItemForm,
    detailOpen,
    selectedOrder,
    servicePackages,
    tenantSettings.tenant_id,
  ]);

  const addItemLineEstimateCents = useMemo(() => {
    const quantity = Number(addItemForm.values.quantity) || 1;

    if (addItemForm.values.item_type === "service") {
      const service = filteredServices.find(
        (item) => String(item.id) === addItemForm.values.service_id
      );
      if (!service) {
        return null;
      }
      const usesClub = clubBenefitPreview?.covered === true && quantity === 1;
      const usesCredit =
        !usesClub &&
        addItemForm.values.use_credit &&
        (serviceCreditSessions ?? 0) > 0 &&
        quantity === 1;
      return usesClub || usesCredit ? 0 : service.price * quantity;
    }

    if (addItemForm.values.item_type === "retail") {
      const product = filteredProducts.find(
        (item) => String(item.id) === addItemForm.values.product_id
      );
      if (!product || product.sale_price == null) {
        return null;
      }
      return product.sale_price * quantity;
    }

    if (addItemForm.values.item_type === "package") {
      const pkg = servicePackages.find(
        (item) => String(item.id) === addItemForm.values.service_package_id
      );
      if (!pkg) {
        return null;
      }
      return pkg.price * quantity;
    }

    return null;
  }, [
    addItemForm.values.item_type,
    addItemForm.values.service_id,
    addItemForm.values.product_id,
    addItemForm.values.service_package_id,
    addItemForm.values.quantity,
    addItemForm.values.use_credit,
    filteredServices,
    filteredProducts,
    servicePackages,
    serviceCreditSessions,
    clubBenefitPreview,
  ]);

  const addItemCreditComparison = useMemo(() => {
    if (addItemForm.values.item_type !== "service") {
      return null;
    }

    const quantity = Number(addItemForm.values.quantity) || 1;
    const service = filteredServices.find(
      (item) => String(item.id) === addItemForm.values.service_id
    );

    if (!service || (serviceCreditSessions ?? 0) <= 0 || quantity !== 1) {
      return null;
    }

    if (!selectedOrder) {
      return null;
    }

    const withoutCreditLine = service.price * quantity;
    const withCreditLine = 0;

    return {
      withoutCreditLine,
      withCreditLine,
      withoutCreditOrderTotal: selectedOrder.total_amount + withoutCreditLine,
      withCreditOrderTotal: selectedOrder.total_amount + withCreditLine,
    };
  }, [
    addItemForm.values.item_type,
    addItemForm.values.service_id,
    addItemForm.values.quantity,
    filteredServices,
    serviceCreditSessions,
    selectedOrder,
  ]);

  const addItemEstimatedOrderTotalCents = useMemo(() => {
    if (!selectedOrder || addItemLineEstimateCents == null) {
      return null;
    }
    return selectedOrder.total_amount + addItemLineEstimateCents;
  }, [selectedOrder, addItemLineEstimateCents]);

  const visibleProfessionals = useMemo(() => {
    if (
      addItemForm.values.item_type !== "service" ||
      !restrictProfessionalServices ||
      !serviceEnablement?.restrict_mode
    ) {
      return professionals;
    }

    const enabledIds = new Set(
      serviceEnablement.professionals
        .filter((professional) => professional.is_enabled)
        .map((professional) => professional.id)
    );

    return professionals.filter((professional) => enabledIds.has(professional.id));
  }, [
    addItemForm.values.item_type,
    professionals,
    restrictProfessionalServices,
    serviceEnablement,
  ]);

  useEffect(() => {
    const serviceId = addItemForm.values.service_id;

    if (
      addItemForm.values.item_type !== "service" ||
      !serviceId ||
      !restrictProfessionalServices
    ) {
      setServiceEnablement(null);
      return;
    }

    let cancelled = false;
    setLoadingServiceEnablement(true);

    getServiceProfessionals(Number(serviceId))
      .then((response) => {
        if (!cancelled) {
          setServiceEnablement(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setServiceEnablement(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingServiceEnablement(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    addItemForm.values.item_type,
    addItemForm.values.service_id,
    restrictProfessionalServices,
  ]);

  useEffect(() => {
    const clientId = selectedOrder?.client_id;
    if (!clientId) {
      setClientLoyaltyCache([]);
      return;
    }

    let cancelled = false;
    getClientLoyalty(clientId)
      .then((response) => {
        if (!cancelled) {
          setClientLoyaltyCache(response.data ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setClientLoyaltyCache([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedOrder?.client_id]);

  const addItemType = addItemForm.values.item_type;
  const addItemServiceId = addItemForm.values.service_id;
  const setAddItemFieldValue = addItemForm.setFieldValue;

  useEffect(() => {
    const serviceId = addItemServiceId;
    const clientId = selectedOrder?.client_id;

    if (addItemType !== "service" || !serviceId || !clientId) {
      setServiceCreditSessions(null);
      setProtocolCreditHint(null);
      setServiceLoyaltyFree(0);
      setServiceLoyaltyRemaining(null);
      setServiceLoyaltyValidityDays(null);
      setServiceLoyaltyExpiresLabel(null);
      setClubBenefitPreview(null);
      return;
    }

    let cancelled = false;
    setLoadingServiceCredits(true);

    const loyaltyCard = clientLoyaltyCache.find(
      (card) =>
        card.service_id === Number(serviceId) ||
        (card.service_ids ?? []).includes(Number(serviceId))
    );
    setServiceLoyaltyRemaining(
      loyaltyCard?.remaining_to_reward != null
        ? loyaltyCard.remaining_to_reward
        : null
    );
    setServiceLoyaltyValidityDays(
      loyaltyCard?.reward_validity_days != null
        ? loyaltyCard.reward_validity_days
        : null
    );

    Promise.all([
      getClientCredits(clientId, {
        service_id: Number(serviceId),
        per_page: 50,
      }),
      getClientClubBenefitPreview(clientId, Number(serviceId)).catch(() => null),
    ])
      .then(([creditsResponse, clubPreview]) => {
        if (cancelled) {
          return;
        }
        const total = creditsResponse.data.reduce(
          (sum, credit) => sum + (credit.sessions_remaining ?? 0),
          0
        );
        const loyaltyLots = creditsResponse.data.filter(
          (credit) => credit.source === "loyalty"
        );
        const loyaltyFree = loyaltyLots.reduce(
          (sum, credit) => sum + (credit.sessions_remaining ?? 0),
          0
        );
        const soonestExpiry = loyaltyLots
          .map((credit) => credit.expires_at)
          .filter((value): value is string => Boolean(value))
          .sort()[0];
        setServiceCreditSessions(total);
        setServiceLoyaltyFree(loyaltyFree);
        setServiceLoyaltyExpiresLabel(
          soonestExpiry
            ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
                new Date(soonestExpiry)
              )
            : null
        );
        const consumption = creditsResponse.meta?.protocol_credit_consumption ?? null;
        if (consumption) {
          setProtocolCreditHint({
            protocol_name: consumption.protocol_name,
            session_number: consumption.session_number,
          });
          setAddItemFieldValue("use_credit", false);
        } else {
          setProtocolCreditHint(null);
        }
        setClubBenefitPreview(
          clubPreview?.data
            ? {
                covered: Boolean(clubPreview.data.covered),
                limit_reached: Boolean(clubPreview.data.limit_reached),
                plan_name: clubPreview.data.plan_name,
                visits_used: clubPreview.data.visits_used,
                visits_limit: clubPreview.data.visits_limit,
              }
            : {
                covered: false,
                limit_reached: false,
                plan_name: null,
                visits_used: null,
                visits_limit: null,
              }
        );
      })
      .catch(() => {
        if (!cancelled) {
          setServiceCreditSessions(null);
          setProtocolCreditHint(null);
          setServiceLoyaltyFree(0);
          setClubBenefitPreview(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingServiceCredits(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    addItemType,
    addItemServiceId,
    setAddItemFieldValue,
    selectedOrder?.client_id,
    clientLoyaltyCache,
  ]);

  useEffect(() => {
    if (!serviceEnablement?.restrict_mode) {
      return;
    }

    const selectedId = Number(addItemForm.values.professional_id);

    if (
      selectedId &&
      !visibleProfessionals.some((professional) => professional.id === selectedId)
    ) {
      addItemForm.setFieldValue("professional_id", "");
    }
  }, [addItemForm, serviceEnablement, visibleProfessionals]);

  const handleRemoveItem = async (item: OrderItem) => {
    if (!selectedOrder) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await removeItem(selectedOrder.id, item.id);
      toast.success(response.message || t("studio.orders.toast.removeItemSuccess"));
      await refreshOrderDetail(selectedOrder.id);
      await loadOrders();
    } catch (error: any) {
      toast.error(error?.data || t("studio.orders.toast.removeItemError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEditNotes = (item: OrderItem) => {
    setInlinePriceItemId(null);
    setInlinePriceDisplay("");
    setEditingItem(item);
    setEditNotesValue(item.execution_notes ?? "");
  };

  const handleCloseEditNotes = () => {
    setEditingItem(null);
    setEditNotesValue("");
  };

  const handleCancelInlinePrice = () => {
    setInlinePriceItemId(null);
    setInlinePriceDisplay("");
  };

  const handleStartInlinePrice = (item: OrderItem) => {
    const priceEditable =
      selectedOrder?.status === "open" &&
      item.item_type === "service" &&
      !item.paid_via_credit &&
      !item.paid_via_subscription;

    if (!priceEditable) {
      return;
    }

    setEditingItem(null);
    setEditNotesValue("");
    setInlinePriceItemId(item.id);
    setInlinePriceDisplay(
      formatCentsToMoneyInput(item.unit_price, i18n.language)
    );
  };

  const handleSaveInlinePrice = async (item: OrderItem) => {
    const priceEditable =
      selectedOrder?.status === "open" &&
      item.item_type === "service" &&
      !item.paid_via_credit &&
      !item.paid_via_subscription;

    if (!selectedOrder || !priceEditable) {
      return;
    }

    const unitPrice = parseMoneyInputToCents(
      inlinePriceDisplay,
      i18n.language
    );

    if (unitPrice === item.unit_price) {
      handleCancelInlinePrice();
      return;
    }

    const catalogMin = item.service?.price;
    if (catalogMin != null && unitPrice < catalogMin) {
      const result = await Swal.fire({
        icon: "question",
        title: t("orders.items.confirmBelowMin.title"),
        text: t("orders.items.confirmBelowMin.body", {
          amount: formatMoney(unitPrice),
          min: formatMoney(catalogMin),
        }),
        showCancelButton: true,
        confirmButtonText: t("orders.items.confirmBelowMin.confirm"),
        cancelButtonText: t("studio.orders.actions.cancel"),
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    setSubmitting(true);

    try {
      const response = await updateItem(selectedOrder.id, item.id, {
        unit_price: unitPrice,
      });
      toast.success(
        response.message || t("orders.items.toast.updatePriceSuccess")
      );
      handleCancelInlinePrice();
      await refreshOrderDetail(selectedOrder.id);
      await loadOrders();
    } catch (error: any) {
      toast.error(error?.data || t("orders.items.toast.updatePriceError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEditNotes = async () => {
    if (!selectedOrder || !editingItem) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await updateItem(selectedOrder.id, editingItem.id, {
        execution_notes: editNotesValue.trim() || null,
      });
      toast.success(
        response.message || t("orders.items.toast.updateNotesSuccess")
      );
      handleCloseEditNotes();
      await refreshOrderDetail(selectedOrder.id);
    } catch (error: any) {
      toast.error(error?.data || t("orders.items.toast.updateNotesError"));
    } finally {
      setSubmitting(false);
    }
  };

  const mapConsumptionLinesToPayload = (): CheckoutConsumptionItem[] =>
    consumptionLines.map((line) => ({
      product_id: line.product_id,
      quantity_consumed: line.quantity_consumed,
    }));

  const handleCheckout = async (
    consumptions: CheckoutConsumptionItem[],
    payments: CheckoutPaymentLine[]
  ) => {
    if (!selectedOrder || payments.length === 0) {
      return;
    }

    setSubmitting(true);

    const usesPixGateway = payments.some((payment) => payment.payment_method === "pix_gateway");
    const usesTerminalGateway = payments.some(
      (payment) => payment.payment_method === "card_terminal_gateway"
    );
    const terminalType =
      payments.find((payment) => payment.payment_method === "card_terminal_gateway")
        ?.terminal_card_type ?? null;
    const rememberedMethod =
      payments.length === 1 ? payments[0].payment_method : payments[0]?.payment_method;

    try {
      const response = await checkout(selectedOrder.id, {
        payments: payments.map(({ payment_method, amount_cents }) => ({
          payment_method,
          amount_cents,
        })),
        ...(terminalType ? { terminal_card_type: terminalType } : {}),
        ...(consumptions.length > 0 ? { consumptions } : {}),
      });

      if (tenantSettings.tenant_id > 0 && rememberedMethod) {
        setLastOrderPaymentMethod(tenantSettings.tenant_id, rememberedMethod);
      }

      if (usesPixGateway && response.data.pix) {
        if (response.message) {
          toast.success(response.message);
        }

        closePaymentModal();
        markOrderPixPending(selectedOrder.id);
        setPixOrderId(selectedOrder.id);
        setPixQrCodeBase64(response.data.pix.qr_code_base64);
        setPixCopyPasteString(response.data.pix.copy_paste_string);
        setPixModalOpen(true);
        await refreshOrderDetail(selectedOrder.id);
        await loadOrders();
        return;
      }

      if (usesTerminalGateway && response.data.terminal) {
        if (response.message) {
          toast.success(response.message);
        }

        closePaymentModal();
        setTerminalOrderId(selectedOrder.id);
        setTerminalCardType(
          response.data.terminal.card_type ?? terminalType
        );
        setTerminalModalOpen(true);
        await refreshOrderDetail(selectedOrder.id);
        await loadOrders();
        return;
      }

      if (response.message) {
        toast.success(response.message);
      } else {
        toast.success(t("studio.orders.toast.checkoutSuccess"));
      }

      closePaymentModal();
      closeCheckoutPrompt();
      closeDetail();
      await loadOrders();
    } catch (error: any) {
      const errorMessage =
        typeof error?.data === "string" && error.data.length > 0
          ? error.data
          : t("studio.orders.toast.checkoutError");
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentConfirm = (payments: CheckoutPaymentLine[]) => {
    void handleCheckout(pendingCheckoutConsumptions, payments);
  };

  const handleVoidOrder = async () => {
    if (!selectedOrder || selectedOrder.status !== "paid") {
      return;
    }

    if (!userHasAnyRole(["owner", "manager"])) {
      toast.error(t("studio.orders.void.forbidden"));
      return;
    }

    let previewHtml = `<p class="mb-3">${t("studio.orders.void.confirmBody")}</p>`;
    let willAttemptGatewayRefund = false;
    let gatewayRefundAmountCents = 0;

    try {
      const previewResponse = await getVoidPreview(selectedOrder.id);
      const preview = previewResponse.data;
      willAttemptGatewayRefund = Boolean(preview.will_attempt_gateway_refund);
      gatewayRefundAmountCents = preview.gateway_refund_amount_cents ?? 0;
      const retailLines =
        preview.retail_stock.length > 0
          ? `<ul class="text-start mb-2">${preview.retail_stock
              .map(
                (line) =>
                  `<li>${line.product_name}: +${line.quantity}</li>`
              )
              .join("")}</ul>`
          : `<p class="text-muted small mb-2">${t("studio.orders.void.preview.noRetailStock")}</p>`;

      const notesHtml =
        preview.notes.length > 0
          ? `<ul class="text-start small text-muted mb-2">${preview.notes
              .map((note) => `<li>${note}</li>`)
              .join("")}</ul>`
          : "";

      previewHtml = `
        <p class="mb-2">${t("studio.orders.void.confirmBody")}</p>
        <div class="text-start border rounded p-2 mb-3 bg-light">
          <p class="mb-2 fw-semibold">${t("studio.orders.void.preview.checklistTitle")}</p>
          <ul class="list-unstyled mb-2">
            <li class="mb-2">
              <span style="display:inline-block;width:1.5rem;color:#f7b84b;">●</span>
              <strong>${t("studio.orders.void.preview.commission")}</strong>:
              ${formatMoney(preview.commission_total_cents)} (${preview.commission_lines})
            </li>
            <li class="mb-2">
              <span style="display:inline-block;width:1.5rem;color:#3577f1;">●</span>
              <strong>${t("studio.orders.void.preview.payments")}</strong>:
              ${preview.payments_count} · ${formatMoney(preview.payments_sum_cents)}
            </li>
            <li class="mb-2">
              <span style="display:inline-block;width:1.5rem;color:#299cdb;">●</span>
              <strong>${t("studio.orders.void.preview.credits")}</strong>:
              ${preview.credit_debit_lines}
            </li>
            <li class="mb-2">
              <span style="display:inline-block;width:1.5rem;color:#6559cc;">●</span>
              <strong>${t("studio.orders.void.preview.packages")}</strong>:
              ${preview.package_grant_lots}
            </li>
            ${
              preview.will_attempt_gateway_refund
                ? `<li class="mb-2">
              <span style="display:inline-block;width:1.5rem;color:#0ab39c;">●</span>
              <strong>${t("studio.orders.void.preview.pixRefund")}</strong>:
              ${formatMoney(preview.gateway_refund_amount_cents ?? 0)}
              (${preview.gateway_refund_payments_count ?? 0})
            </li>`
                : `<li class="mb-2 text-muted">
              <span style="display:inline-block;width:1.5rem;color:#adb5bd;">○</span>
              ${t("studio.orders.void.preview.pixRefundNone")}
            </li>`
            }
            <li class="mb-2">
              <span style="display:inline-block;width:1.5rem;color:#405189;">●</span>
              <strong>${t("studio.orders.void.preview.stockTitle")}</strong>
            </li>
          </ul>
          ${retailLines}
          ${notesHtml}
        </div>
      `;
    } catch {
      previewHtml = `<p class="mb-3">${t("studio.orders.void.confirmBody")}</p>
        <p class="text-muted small">${t("studio.orders.void.preview.loadError")}</p>`;
    }

    const confirmation = await Swal.fire({
      title: t("studio.orders.void.confirmTitle"),
      html: previewHtml,
      input: "textarea",
      inputPlaceholder: t("studio.orders.void.reasonPlaceholder"),
      inputAttributes: {
        maxlength: "500",
        "aria-label": t("studio.orders.void.reasonPlaceholder"),
      },
      showCancelButton: true,
      confirmButtonText: t("studio.orders.void.confirmAction"),
      cancelButtonText: t("studio.orders.actions.cancel"),
      confirmButtonColor: "#d63939",
      width: "32rem",
      inputValidator: (value) => {
        if (!value || value.trim().length < 3) {
          return t("studio.orders.void.reasonRequired");
        }

        return null;
      },
    });

    if (!confirmation.isConfirmed || typeof confirmation.value !== "string") {
      return;
    }

    setSubmitting(true);

    const voidedOrderId = selectedOrder.id;

    try {
      const response = await voidOrder(voidedOrderId, {
        reason: confirmation.value.trim(),
      });

      setStatusFilter("voided");
      navigate(`/orders/${voidedOrderId}?status=voided`, { replace: true });

      const successMessage = willAttemptGatewayRefund
        ? t("studio.orders.void.successWithPix", {
            amount: formatMoney(gatewayRefundAmountCents),
          })
        : response.message || t("studio.orders.void.success");

      toast.success(
        <div>
          <div>{successMessage}</div>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => {
              navigate(`/orders/${voidedOrderId}?status=voided`);
              void refreshOrderDetail(voidedOrderId);
            }}
          >
            {t("studio.orders.void.viewVoided")}
          </button>
        </div>,
        { autoClose: 8000 }
      );

      await refreshOrderDetail(voidedOrderId);
      await loadOrders();
    } catch (error: any) {
      const errorMessage =
        typeof error?.data === "string" && error.data.length > 0
          ? error.data
          : willAttemptGatewayRefund
            ? t("studio.orders.void.errorPixRefund")
            : t("studio.orders.void.error");
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePixPaymentConfirmed = async () => {
    if (pixOrderId !== null) {
      clearOrderPixPending(pixOrderId);
    }

    closePixModal();
    closePaymentModal();
    closeCheckoutPrompt();
    closeDetail();
    await loadOrders();
  };

  const handleTerminalPaymentConfirmed = async () => {
    closeTerminalModal();
    closePaymentModal();
    closeCheckoutPrompt();
    closeDetail();
    await loadOrders();
  };

  const handleTerminalPaymentCancelled = async () => {
    if (terminalOrderId !== null) {
      await refreshOrderDetail(terminalOrderId);
    }
    await loadOrders();
  };

  const handleFinalizeOrder = async () => {
    if (!selectedOrder || loadingSettings) {
      return;
    }

    if (tenantSettings.prompt_consumption_on_checkout) {
      await openCheckoutPrompt();
      return;
    }

    void openPaymentModal([]);
  };

  const handleAddConsumptionLine = () => {
    if (!draftProductId) {
      setConsumptionDraftError(t("studio.orders.checkout_prompt.validation.productRequired"));
      return;
    }

    if (draftQuantity < 1 || Number.isNaN(draftQuantity)) {
      setConsumptionDraftError(t("studio.orders.checkout_prompt.validation.quantityMin"));
      return;
    }

    const product = internalProducts.find((item) => item.id === Number(draftProductId));

    if (!product) {
      setConsumptionDraftError(t("studio.orders.checkout_prompt.validation.productRequired"));
      return;
    }

    setConsumptionLines((previous) => [
      ...previous,
      {
        key: `${product.id}-${Date.now()}`,
        product_id: product.id,
        product_name: product.name,
        quantity_consumed: draftQuantity,
      },
    ]);
    setDraftProductId("");
    setDraftQuantity(1);
    setConsumptionDraftError(null);
  };

  const handleRemoveConsumptionLine = (key: string) => {
    setConsumptionLines((previous) => previous.filter((line) => line.key !== key));
  };

  const resolveItemDescription = (item: OrderItem): string => {
    if (item.item_type === "service") {
      return item.service?.name ?? notAvailable;
    }

    if (item.item_type === "package") {
      const packageName = item.service_package?.name ?? notAvailable;
      const sessions = item.service_package?.sessions_count;
      return sessions
        ? t("studio.orders.itemDescription.package", {
            name: packageName,
            sessions,
          })
        : packageName;
    }

    return item.product?.name ?? notAvailable;
  };

  const resolveItemTypeLabel = (itemType: OrderItem["item_type"]): string => {
    if (itemType === "service") {
      return t("studio.orders.itemType.service");
    }
    if (itemType === "package") {
      return t("studio.orders.itemType.package");
    }
    return t("studio.orders.itemType.retail");
  };

  const resolveStatusLabel = (status: string): string => {
    if (status === "paid") {
      return t("studio.orders.status.paid");
    }

    if (status === "voided") {
      return t("studio.orders.status.voided");
    }

    return t("studio.orders.status.open");
  };

  const resolveStatusColor = (status: string): string => {
    if (status === "paid") {
      return "success";
    }

    if (status === "voided") {
      return "secondary";
    }

    return "warning";
  };

  const resolvePaidSummary = useCallback(
    (order: Order): string | null => {
      if (order.status !== "paid") {
        return null;
      }

      const activePayments = (order.payments ?? []).filter((payment) => !payment.voided_at);
      const method = activePayments[0]?.payment_method;
      const paidAt = order.paid_at ?? activePayments[0]?.paid_at ?? null;

      if (activePayments.length > 1 && paidAt) {
        return t("studio.orders.checkout.payment.paidSummarySplit", {
          count: activePayments.length,
          paidAt: formatDateTime(paidAt),
        });
      }

      if (method && paidAt) {
        return t("studio.orders.checkout.payment.paidSummary", {
          method: t(`studio.orders.checkout.payment.badge.${method}`),
          paidAt: formatDateTime(paidAt),
        });
      }

      if (method) {
        return t(`studio.orders.checkout.payment.badge.${method}`);
      }

      if (paidAt) {
        return t("studio.orders.payment.paidAtOnly", {
          paidAt: formatDateTime(paidAt),
        });
      }

      return null;
    },
    [formatDateTime, t]
  );

  const isOrderReadOnly =
    selectedOrder?.status === "paid" || selectedOrder?.status === "voided";
  const canVoidSelectedOrder =
    selectedOrder?.status === "paid" && userHasAnyRole(["owner", "manager"]);
  const canApplyOrderDiscount =
    userHasAnyRole(["owner", "manager"]) ||
    (userHasAnyRole(["receptionist"]) &&
      Boolean(tenantSettings.allow_receptionist_discount));
  const discountCapBps =
    userHasAnyRole(["receptionist"]) && !userHasAnyRole(["owner", "manager"])
      ? 500
      : tenantSettings.max_manual_discount_bps ?? 2000;

  const canEditItemExecutionNotes = useCallback(
    (item: OrderItem): boolean => {
      if (item.item_type !== "service" || !selectedOrder) {
        return false;
      }

      if (selectedOrder.status === "open") {
        return true;
      }

      if (selectedOrder.status === "paid" || selectedOrder.status === "voided") {
        return userHasAnyRole(["owner", "manager"]);
      }

      return false;
    },
    [selectedOrder]
  );

  const canEditItemUnitPrice = useCallback(
    (item: OrderItem): boolean => {
      if (!selectedOrder || selectedOrder.status !== "open") {
        return false;
      }

      if (item.item_type !== "service") {
        return false;
      }

      return !item.paid_via_credit && !item.paid_via_subscription;
    },
    [selectedOrder]
  );

  const ordersGuideUrl = useMemo(
    () => getHelpArticleUrl(HELP_ARTICLE_ORDERS, i18n.language),
    [i18n.language]
  );

  const listDateColumnLabel = useMemo(() => {
    switch (statusFilter) {
      case "paid":
        return t("studio.orders.table.paidAt");
      case "voided":
        return t("studio.orders.table.voidedAt");
      case "all":
        return t("studio.orders.table.when");
      default:
        return t("studio.orders.table.createdAt");
    }
  }, [statusFilter, t]);

  const listDateValue = useCallback(
    (order: Order): string => {
      if (statusFilter === "paid" && order.paid_at) {
        return order.paid_at;
      }

      if (statusFilter === "voided" && order.voided_at) {
        return order.voided_at;
      }

      if (statusFilter === "all") {
        return order.paid_at || order.voided_at || order.created_at;
      }

      return order.created_at;
    },
    [statusFilter]
  );

  const visibleOrdersTotalCents = useMemo(
    () => orders.reduce((sum, order) => sum + (order.total_amount ?? 0), 0),
    [orders]
  );

  const oldestVisibleOrderAt = useMemo(() => {
    if (orders.length === 0) {
      return null;
    }

    return orders.reduce((oldest, order) => {
      const candidate = listDateValue(order);
      return candidate < oldest ? candidate : oldest;
    }, listDateValue(orders[0]));
  }, [listDateValue, orders]);

  const chromeListSummary = useMemo(() => {
    const count = meta?.total ?? orders.length;
    const totalLabel = formatMoney(visibleOrdersTotalCents);

    if (!oldestVisibleOrderAt) {
      return t("studio.orders.chrome.summaryEmpty", {
        count,
        total: totalLabel,
      });
    }

    const oldestLabel = formatOldestDate(oldestVisibleOrderAt);

    switch (statusFilter) {
      case "paid":
        return t("studio.orders.chrome.summaryPaid", {
          count,
          total: totalLabel,
          oldest: oldestLabel,
        });
      case "voided":
        return t("studio.orders.chrome.summaryVoided", {
          count,
          total: totalLabel,
          oldest: oldestLabel,
        });
      case "all":
        return t("studio.orders.chrome.summaryAll", {
          count,
          total: totalLabel,
          oldest: oldestLabel,
        });
      default:
        return t("studio.orders.chrome.summaryOpen", {
          count,
          total: totalLabel,
          oldest: oldestLabel,
        });
    }
  }, [
    formatMoney,
    formatOldestDate,
    meta?.total,
    oldestVisibleOrderAt,
    orders.length,
    statusFilter,
    t,
    visibleOrdersTotalCents,
  ]);

  const emptyMessage = useMemo(() => {
    if (hasDiscountFilter || appliedByUserId !== "") {
      return t("studio.orders.empty.noCourtesy");
    }

    if (paymentMethodFilter) {
      return t("studio.orders.empty.noPaymentMethod", {
        method: t(`studio.orders.checkout.payment.method.${paymentMethodFilter}`),
      });
    }

    switch (statusFilter) {
      case "paid":
        return t("studio.orders.empty.noPaid");
      case "all":
        return t("studio.orders.empty.noOrders");
      default:
        return t("studio.orders.empty.noData");
    }
  }, [appliedByUserId, hasDiscountFilter, paymentMethodFilter, statusFilter, t]);

  const paginationFrom = meta?.from ?? 0;
  const paginationTo = meta?.to ?? 0;
  const paginationTotal = meta?.total ?? 0;
  const paginationLastPage = meta?.last_page ?? 1;

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("studio.orders.breadcrumb.title")}
          pageTitle={t("studio.orders.breadcrumb.pageTitle")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="orders-card-header--chrome">
                <div className="orders-chrome-row orders-chrome-row--primary">
                  <div className="orders-chrome-title">
                    <h4 className="card-title mb-0">
                      {t("studio.orders.breadcrumb.title")}
                    </h4>
                    <p className="orders-chrome-summary text-muted small mb-0 mt-1">
                      {chromeListSummary}
                    </p>
                  </div>
                  <div className="orders-chrome-toolbar">
                    <Button color="success" size="sm" onClick={() => setNewOrderOpen(true)}>
                      <i className="ri-add-line align-bottom me-1" />
                      {t("studio.orders.actions.new")}
                    </Button>
                    <OrdersOptionsMenu
                      guideUrl={ordersGuideUrl}
                      exportingCsv={exportingCsv}
                      exportDisabled={loading}
                      lastAction={lastOptionsAction}
                      onRememberAction={handleRememberOptionsAction}
                      onExportCsv={() => void handleExportCsv()}
                    />
                  </div>
                </div>
                <div className="orders-chrome-row">
                  <Nav pills className="orders-view-tabs nav-success mb-0">
                    {(
                      [
                        ["open", "studio.orders.filters.open"],
                        ["paid", "studio.orders.filters.paid"],
                        ["voided", "studio.orders.filters.voided"],
                        ["all", "studio.orders.filters.all"],
                      ] as const
                    ).map(([value, labelKey]) => (
                      <NavItem key={value}>
                        <NavLink
                          className={statusFilter === value ? "active" : ""}
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            handleStatusFilterChange(value);
                          }}
                        >
                          {t(labelKey)}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                  <OrdersFiltersPanel
                    paymentMethodFilter={paymentMethodFilter}
                    paymentMethodOptions={visiblePaymentMethodFilterOptions}
                    loadingPaymentMethods={loadingPaymentMethodFilters}
                    hasDiscountFilter={hasDiscountFilter}
                    appliedByUserId={appliedByUserId}
                    canFilterByApplier={canFilterByApplier}
                    teamUsers={teamUsers}
                    sortFilter={sortFilter}
                    onPaymentMethodChange={handlePaymentMethodFilterChange}
                    onHasDiscountChange={handleHasDiscountFilterChange}
                    onAppliedByChange={(value) => {
                      setAppliedByUserId(value);
                      if (value !== "") {
                        setHasDiscountFilter(true);
                      }
                    }}
                    onSortChange={handleSortFilterChange}
                    onClearFilters={handleClearAdvancedFilters}
                  />
                </div>
              </CardHeader>
              <CardBody>
                {pendingPackageBannerId ? (
                  <Alert
                    color="info"
                    className="d-flex flex-wrap align-items-center justify-content-between gap-2"
                    toggle={() => {
                      if (tenantSettings.tenant_id > 0) {
                        clearPendingPackageSale(tenantSettings.tenant_id);
                      }
                      setPendingPackageBannerId(null);
                    }}
                  >
                    <span className="mb-0">
                      {t("studio.orders.alerts.pendingPackageBanner", {
                        id: pendingPackageBannerId,
                      })}
                    </span>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setNewOrderOpen(true)}
                    >
                      {t("studio.orders.actions.new")}
                    </Button>
                  </Alert>
                ) : null}
                {hasDiscountFilter || appliedByUserId !== "" ? (
                  <Alert
                    color="success"
                    className="d-flex flex-wrap align-items-center justify-content-between gap-2"
                  >
                    <span className="mb-0">
                      {t("studio.orders.alerts.courtesyFilterActive")}
                      {meta?.courtesy_summary &&
                      meta.courtesy_summary.orders_count > 0
                        ? ` ${t("studio.orders.alerts.courtesySummary", {
                            count: meta.courtesy_summary.orders_count,
                            total: formatMoney(
                              meta.courtesy_summary.discount_total_cents
                            ),
                          })}`
                        : ""}
                    </span>
                    <Button
                      color="success"
                      outline
                      size="sm"
                      onClick={() => {
                        setHasDiscountFilter(false);
                        setAppliedByUserId("");
                      }}
                    >
                      {t("studio.orders.actions.clearCourtesyFilter")}
                    </Button>
                  </Alert>
                ) : null}
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary">{t("studio.orders.loading")}</Spinner>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">{emptyMessage}</p>
                    {hasDiscountFilter || appliedByUserId !== "" ? (
                      <Button
                        color="primary"
                        outline
                        onClick={() => {
                          setHasDiscountFilter(false);
                          setAppliedByUserId("");
                        }}
                      >
                        {t("studio.orders.actions.clearCourtesyFilter")}
                      </Button>
                    ) : null}
                    {!hasDiscountFilter &&
                    appliedByUserId === "" &&
                    statusFilter === "open" ? (
                      <Button color="primary" onClick={() => setNewOrderOpen(true)}>
                        {t("studio.orders.actions.createFirst")}
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0 orders-table">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.orders.table.id")}</th>
                            <th>{t("studio.orders.table.client")}</th>
                            <th>{t("studio.orders.table.status")}</th>
                            <th>{listDateColumnLabel}</th>
                            <th>{t("studio.orders.table.total")}</th>
                            <th
                              className="text-end"
                              aria-label={t("studio.orders.table.actions")}
                            />
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order) => {
                            const subtotal =
                              order.subtotal_amount ?? order.total_amount;
                            const discount = order.discount_total_cents ?? 0;
                            const highlightRatio =
                              (tenantSettings.courtesy_highlight_bps ?? 1500) /
                              10000;
                            const highCourtesy =
                              discount > 0 &&
                              subtotal > 0 &&
                              discount / subtotal >= highlightRatio;
                            const dateValue = listDateValue(order);

                            return (
                            <tr
                              key={order.id}
                              className={highCourtesy ? "table-warning" : undefined}
                              onClick={() => openDetail(order)}
                            >
                              <td>{order.id}</td>
                              <td className="orders-table__client">
                                {order.client?.name ?? notAvailable}
                              </td>
                              <td>
                                <Badge color={resolveStatusColor(order.status)} className="text-uppercase">
                                  {resolveStatusLabel(order.status)}
                                </Badge>
                                {order.status === "open" &&
                                order.needs_health_record &&
                                canManageClinicalRecords() ? (
                                  <Badge color="warning" className="ms-1 fw-normal">
                                    {t("studio.orders.anamnesis.listChip")}
                                  </Badge>
                                ) : null}
                                {(order.discount_total_cents ?? 0) > 0 ? (
                                  <Badge color="soft-info" className="ms-1 fw-normal">
                                    {t("studio.orders.discount.courtesyBadge")}
                                  </Badge>
                                ) : null}
                                {orderHasPendingPix(order) && (
                                  <Badge color="warning" className="ms-1 fw-normal">
                                    {t("studio.orders.checkout.payment.awaitingPixBadge")}
                                  </Badge>
                                )}
                                {order.status === "paid" && resolvePaidSummary(order) && (
                                  <Badge color="soft-success" className="ms-1 fw-normal">
                                    {resolvePaidSummary(order)}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <span>{formatDateTime(dateValue)}</span>
                                <span className="orders-table__relative">
                                  {formatRelativeTime(dateValue)}
                                </span>
                              </td>
                              <td>
                                <div>{formatMoney(order.total_amount)}</div>
                                {(order.total_amount ?? 0) === 0 ? (
                                  <span className="orders-table__zero-hint">
                                    {t("studio.orders.table.noEntries")}
                                  </span>
                                ) : null}
                                {(order.discount_total_cents ?? 0) > 0 ? (
                                  <div className="text-success small">
                                    {t("studio.orders.discount.listDiscount", {
                                      amount: formatMoney(order.discount_total_cents ?? 0),
                                    })}
                                    {order.discounts?.[0]?.applied_by?.name ? (
                                      <div className="text-muted">
                                        {t("studio.orders.discount.listAppliedBy", {
                                          name: order.discounts[0].applied_by.name,
                                        })}
                                      </div>
                                    ) : null}
                                  </div>
                                ) : null}
                              </td>
                              <td className="text-end">
                                <Button
                                  color="secondary"
                                  outline
                                  size="sm"
                                  className="orders-table__open-btn"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openDetail(order);
                                  }}
                                >
                                  {t("studio.orders.actions.open")}
                                </Button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={4} />
                            <td className="orders-table__footer-total">
                              {t("studio.orders.table.footerTotal", {
                                total: formatMoney(visibleOrdersTotalCents),
                              })}
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                    {meta && paginationLastPage > 1 && (
                      <Row className="align-items-center mt-3">
                        <Col sm={6}>
                          <div className="text-muted">
                            {t("studio.orders.pagination.showing", {
                              from: paginationFrom,
                              to: paginationTo,
                              total: paginationTotal,
                            })}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <Pagination className="pagination-separated justify-content-sm-end mb-0">
                            <PaginationItem disabled={page <= 1}>
                              <PaginationLink
                                previous
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page > 1) {
                                    setPage(page - 1);
                                  }
                                }}
                              >
                                {t("studio.orders.pagination.previous")}
                              </PaginationLink>
                            </PaginationItem>
                            <PaginationItem disabled={page >= paginationLastPage}>
                              <PaginationLink
                                next
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page < paginationLastPage) {
                                    setPage(page + 1);
                                  }
                                }}
                              >
                                {t("studio.orders.pagination.next")}
                              </PaginationLink>
                            </PaginationItem>
                          </Pagination>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Offcanvas
        isOpen={newOrderOpen}
        toggle={() => setNewOrderOpen(false)}
        direction="end"
        className="border-0"
      >
        <OffcanvasHeader toggle={() => setNewOrderOpen(false)}>
          {t("studio.orders.form.newTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          <Form onSubmit={newOrderForm.handleSubmit}>
            <div className="mb-3">
              <Label htmlFor="order-unit">{t("studio.orders.fields.unit.label")}</Label>
              <Input
                id="order-unit"
                name="unit_id"
                type="select"
                value={newOrderForm.values.unit_id}
                onChange={newOrderForm.handleChange}
                onBlur={newOrderForm.handleBlur}
                invalid={!!(newOrderForm.touched.unit_id && newOrderForm.errors.unit_id)}
              >
                <option value="">{t("studio.orders.fields.unit.placeholder")}</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Input>
              <FormFeedback>{newOrderForm.errors.unit_id}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="order-client">{t("studio.orders.fields.client.label")}</Label>
              <ClientPicker
                id="order-client"
                name="client_id"
                value={newOrderForm.values.client_id}
                onChange={(clientId) => newOrderForm.setFieldValue("client_id", clientId)}
                onBlur={() => newOrderForm.setFieldTouched("client_id", true)}
                invalid={!!(newOrderForm.touched.client_id && newOrderForm.errors.client_id)}
                error={newOrderForm.errors.client_id}
              />
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <Button color="light" onClick={() => setNewOrderOpen(false)}>
                {t("studio.orders.actions.cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? t("studio.orders.loading") : t("studio.orders.actions.new")}
              </Button>
            </div>
          </Form>
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={detailOpen} toggle={closeDetail} size="lg" scrollable>
        <ModalHeader toggle={closeDetail}>
          {t("studio.orders.form.detailTitle", { id: selectedOrder?.id ?? "" })}
        </ModalHeader>
        <ModalBody>
          {detailLoading || !selectedOrder ? (
            <div className="text-center py-4">
              <Spinner color="primary">{t("studio.orders.loading")}</Spinner>
            </div>
          ) : (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p className="text-muted mb-1">{t("studio.orders.table.client")}</p>
                  <p className="fw-medium mb-0">
                    {selectedOrder.client?.name ?? notAvailable}
                  </p>
                  {selectedOrder.needs_health_record &&
                  canManageClinicalRecords() &&
                  selectedOrder.status === "open" ? (
                    <Alert color="warning" className="mt-2 mb-0 py-2">
                      <p className="small mb-2">
                        {t("studio.orders.anamnesis.missingBanner")}
                      </p>
                      <Button
                        color="warning"
                        size="sm"
                        type="button"
                        outline
                        onClick={() => {
                          setAnamnesisServiceId(
                            selectedOrder.health_prompt_service_id ?? null
                          );
                          setAnamnesisOpen(true);
                        }}
                      >
                        <i className="ri-heart-pulse-line me-1" />
                        {t("studio.orders.anamnesis.fillNow")}
                      </Button>
                    </Alert>
                  ) : null}
                </Col>
                <Col md={6} className="text-md-end">
                  <p className="text-muted mb-1">{t("studio.orders.total.label")}</p>
                  {(selectedOrder.discount_total_cents ?? 0) > 0 ? (
                    <div className="mb-1">
                      <p className="text-muted small mb-0">
                        {t("studio.orders.discount.subtotal")}:{" "}
                        {formatMoney(
                          selectedOrder.subtotal_amount ?? selectedOrder.total_amount
                        )}
                      </p>
                      <p className="text-success small mb-0">
                        {t("studio.orders.discount.applied")}: −
                        {formatMoney(selectedOrder.discount_total_cents ?? 0)}
                      </p>
                      <Badge color="soft-info" className="fw-normal mt-1">
                        {t("studio.orders.discount.courtesyBadge")}
                      </Badge>
                      {selectedOrder.discounts?.[0]?.applied_by?.name ? (
                        <p className="text-muted small mb-0 mt-1">
                          {t("studio.orders.discount.appliedBy", {
                            name: selectedOrder.discounts[0].applied_by.name,
                          })}
                        </p>
                      ) : null}
                      {selectedOrder.discounts?.[0]?.applied_at ? (
                        <p className="text-muted small mb-0 mt-1">
                          {t("studio.orders.discount.appliedAt", {
                            when: formatDateTime(
                              selectedOrder.discounts[0].applied_at
                            ),
                          })}
                        </p>
                      ) : null}
                      {selectedOrder.discounts?.[0]?.reason ? (
                        <p className="text-muted small mb-0 mt-1">
                          {t("studio.orders.discount.reasonShown")}:{" "}
                          {selectedOrder.discounts[0].reason}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  <p className="fw-semibold fs-5 mb-0">
                    {formatMoney(selectedOrder.total_amount)}
                  </p>
                  {selectedOrder.status === "paid" && resolvePaidSummary(selectedOrder) && (
                    <div className="mt-2">
                      <Badge color={resolveStatusColor(selectedOrder.status)} className="text-uppercase">
                        {resolveStatusLabel(selectedOrder.status)}
                      </Badge>
                      <Badge color="soft-success" className="ms-1">
                        {resolvePaidSummary(selectedOrder)}
                      </Badge>
                    </div>
                  )}
                  {selectedOrder.status === "voided" && (
                    <div className="mt-2">
                      <Badge color={resolveStatusColor(selectedOrder.status)} className="text-uppercase">
                        {resolveStatusLabel(selectedOrder.status)}
                      </Badge>
                      {selectedOrder.void_reason ? (
                        <p className="text-muted small mt-2 mb-0">
                          {t("studio.orders.void.reasonLabel")}: {selectedOrder.void_reason}
                        </p>
                      ) : null}
                    </div>
                  )}
                  {selectedOrder.status === "open" && orderHasPendingPix(selectedOrder) && (
                    <div className="mt-2">
                      <Badge color={resolveStatusColor(selectedOrder.status)} className="text-uppercase">
                        {resolveStatusLabel(selectedOrder.status)}
                      </Badge>
                      <Badge color="warning" className="ms-1">
                        {t("studio.orders.checkout.payment.awaitingPixBadge")}
                      </Badge>
                    </div>
                  )}
                </Col>
              </Row>

              {(selectedOrder.status === "paid"
                || selectedOrder.status === "voided"
                || (selectedOrder.payments?.length ?? 0) > 0) && (
                <OrderPaymentDetailsCard
                  order={selectedOrder}
                  formatMoney={formatMoney}
                  formatDateTime={formatDateTime}
                />
              )}

              {useCommissionTracking && selectedOrder.status === "open" && (
                <OrderCommissionRateChangedBanner
                  order={selectedOrder}
                  enabled={useCommissionTracking}
                />
              )}

              {useCommissionTracking && selectedOrder.status === "paid" && (
                <OrderCommissionDetailsCard
                  order={selectedOrder}
                  formatMoney={formatMoney}
                />
              )}

              {useCommissionTracking &&
              Boolean(tenantSettings.commission_on_products) &&
              selectedOrder.status === "open" &&
              (selectedOrder.order_items ?? []).some(
                (item) =>
                  item.item_type === "retail" &&
                  (item.professional_id == null || item.professional_id === 0)
              ) ? (
                <Alert color="warning" className="border-0 mb-3">
                  {t("studio.orders.detail.retailWithoutProfessionalHint")}
                </Alert>
              ) : null}

              <h6 className="mb-3">{t("studio.orders.form.itemsTitle")}</h6>
              {(selectedOrder.order_items ?? []).length === 0 ? (
                <p className="text-muted">{t("studio.orders.empty.noItems")}</p>
              ) : (
                <div className="table-responsive mb-4">
                  <Table size="sm" className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("studio.orders.table.itemType")}</th>
                        <th>{t("studio.orders.table.description")}</th>
                        <th>{t("studio.orders.table.quantity")}</th>
                        <th>{t("studio.orders.table.unitPrice")}</th>
                        <th>{t("studio.orders.table.lineTotal")}</th>
                        <th>{t("studio.orders.table.professional")}</th>
                        <th>{t("orders.items.fields.executionNotes.label")}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder.order_items ?? []).map((item) => (
                        <tr key={item.id}>
                          <td>{resolveItemTypeLabel(item.item_type)}</td>
                          <td>
                            <div className="d-flex flex-wrap align-items-center gap-1">
                              <span>{resolveItemDescription(item)}</span>
                              {item.paid_via_subscription ? (
                                <Badge color="soft-primary" className="fw-normal">
                                  {t("studio.orders.badges.paidViaClub")}
                                </Badge>
                              ) : item.paid_via_loyalty ? (
                                <Badge color="soft-success" className="fw-normal">
                                  {t("studio.orders.badges.paidViaLoyalty")}
                                </Badge>
                              ) : item.paid_via_credit ? (
                                <Badge color="soft-info" className="fw-normal">
                                  {t("studio.orders.badges.paidViaCredit")}
                                </Badge>
                              ) : null}
                            </div>
                          </td>
                          <td>{item.quantity}</td>
                          <td style={{ minWidth: "8.5rem" }}>
                            {inlinePriceItemId === item.id &&
                            canEditItemUnitPrice(item) ? (
                              <div className="d-flex flex-column gap-1">
                                <div className="d-flex align-items-center gap-1">
                                  <div className="flex-grow-1" style={{ minWidth: "5.5rem" }}>
                                    <MoneyInput
                                      id={`inline-unit-price-${item.id}`}
                                      value={inlinePriceDisplay}
                                      onChange={setInlinePriceDisplay}
                                      locale={i18n.language}
                                      placeholder={t(
                                        "orders.items.fields.unitPrice.placeholder"
                                      )}
                                      disabled={submitting}
                                      autoFocus
                                      onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                          event.preventDefault();
                                          void handleSaveInlinePrice(item);
                                        } else if (event.key === "Escape") {
                                          event.preventDefault();
                                          handleCancelInlinePrice();
                                        }
                                      }}
                                    />
                                  </div>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="px-2"
                                    disabled={submitting}
                                    title={t("orders.items.actions.savePrice")}
                                    aria-label={t("orders.items.actions.savePrice")}
                                    onClick={() => void handleSaveInlinePrice(item)}
                                  >
                                    <i className="ri-check-line" aria-hidden="true" />
                                  </Button>
                                  <Button
                                    color="light"
                                    size="sm"
                                    className="px-2"
                                    disabled={submitting}
                                    title={t("studio.orders.actions.cancel")}
                                    aria-label={t("studio.orders.actions.cancel")}
                                    onClick={handleCancelInlinePrice}
                                  >
                                    <i className="ri-close-line" aria-hidden="true" />
                                  </Button>
                                </div>
                                {item.service?.price_is_from ? (
                                  <div className="small text-muted">
                                    {t("orders.items.fields.unitPrice.fromHintShort", {
                                      price: formatMoney(item.service.price),
                                    })}
                                  </div>
                                ) : (
                                  <div className="small text-muted">
                                    {t("orders.items.fields.unitPrice.hintShort")}
                                  </div>
                                )}
                              </div>
                            ) : canEditItemUnitPrice(item) ? (
                              <button
                                type="button"
                                className="btn btn-link text-body text-decoration-none p-0 text-start"
                                disabled={submitting}
                                onClick={() => handleStartInlinePrice(item)}
                                title={t("orders.items.actions.editPrice")}
                                aria-label={t("orders.items.actions.editPrice")}
                              >
                                <span className="d-inline-flex align-items-center gap-1">
                                  <span className="fw-medium">
                                    {formatMoney(item.unit_price)}
                                  </span>
                                  <i
                                    className="ri-pencil-line text-muted"
                                    aria-hidden="true"
                                  />
                                </span>
                                {item.service?.price != null &&
                                item.unit_price !== item.service.price ? (
                                  <div className="small text-primary">
                                    {t("orders.items.fields.unitPrice.adjustedShort")}
                                  </div>
                                ) : item.service?.price_is_from ? (
                                  <div className="small text-muted">
                                    {t("orders.items.fields.unitPrice.fromHintShort", {
                                      price: formatMoney(item.service.price),
                                    })}
                                  </div>
                                ) : null}
                              </button>
                            ) : (
                              <>
                                {formatMoney(item.unit_price)}
                                {item.item_type === "service" &&
                                item.service?.price != null &&
                                item.unit_price !== item.service.price ? (
                                  <div className="small text-primary">
                                    {t("orders.items.fields.unitPrice.adjustedShort")}
                                  </div>
                                ) : item.service?.price_is_from ? (
                                  <div className="small text-muted">
                                    {t("orders.items.fields.unitPrice.fromHintShort", {
                                      price: formatMoney(item.service.price),
                                    })}
                                  </div>
                                ) : null}
                              </>
                            )}
                          </td>
                          <td>{formatMoney(item.unit_price * item.quantity)}</td>
                          <td>{item.professional?.name ?? notAvailable}</td>
                          <td>
                            {item.item_type === "service" && item.execution_notes ? (
                              <span
                                className="d-inline-flex align-items-center gap-1 text-muted"
                                title={item.execution_notes}
                              >
                                <i className="ri-file-text-line" aria-hidden="true" />
                                <span className="text-truncate d-inline-block" style={{ maxWidth: "12rem" }}>
                                  {item.execution_notes}
                                </span>
                              </span>
                            ) : (
                              notAvailable
                            )}
                          </td>
                          <td className="text-end">
                            {canEditItemExecutionNotes(item) && (
                              <Button
                                color="soft-primary"
                                size="sm"
                                className="me-1"
                                disabled={submitting}
                                onClick={() => handleOpenEditNotes(item)}
                              >
                                {t("orders.items.actions.editNotes")}
                              </Button>
                            )}
                            {!isOrderReadOnly && (
                              <Button
                                color="soft-danger"
                                size="sm"
                                disabled={submitting}
                                onClick={() => handleRemoveItem(item)}
                              >
                                {t("studio.orders.actions.removeItem")}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {!isOrderReadOnly && (
                <>
              <h6 className="mb-3">{t("studio.orders.form.addItemTitle")}</h6>
              <Form onSubmit={addItemForm.handleSubmit}>
                <Row>
                  <Col md={6} className="mb-3">
                    <Label htmlFor="item-type">{t("studio.orders.fields.itemType.label")}</Label>
                    <Input
                      id="item-type"
                      name="item_type"
                      type="select"
                      value={addItemForm.values.item_type}
                      onChange={(event) => {
                        addItemForm.handleChange(event);
                        addItemForm.setFieldValue("service_id", "");
                        addItemForm.setFieldValue("product_id", "");
                        addItemForm.setFieldValue("service_package_id", "");
                        addItemForm.setFieldValue("execution_notes", "");
                        setAddItemCategoryFilter("all");
                        setAddItemBrandFilter("");
                      }}
                      onBlur={addItemForm.handleBlur}
                    >
                      <option value="service">{t("studio.orders.itemType.service")}</option>
                      <option value="retail">{t("studio.orders.itemType.retail")}</option>
                      <option value="package">{t("studio.orders.itemType.package")}</option>
                    </Input>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label htmlFor="item-quantity">{t("studio.orders.fields.quantity.label")}</Label>
                    <Input
                      id="item-quantity"
                      name="quantity"
                      type="number"
                      min={1}
                      value={addItemForm.values.quantity}
                      onChange={addItemForm.handleChange}
                      onBlur={addItemForm.handleBlur}
                      invalid={!!(addItemForm.touched.quantity && addItemForm.errors.quantity)}
                    />
                    <FormFeedback>{addItemForm.errors.quantity}</FormFeedback>
                  </Col>
                  {useCatalogCategories &&
                  addItemForm.values.item_type !== "package" ? (
                    <Col md={12} className="mb-3">
                      <Label htmlFor="item-category-filter">
                        {t("studio.catalog.category")}
                      </Label>
                      <Input
                        id="item-category-filter"
                        type="select"
                        value={addItemCategoryFilter}
                        onChange={(event) => {
                          setAddItemCategoryFilter(event.target.value);
                          addItemForm.setFieldValue("service_id", "");
                          addItemForm.setFieldValue("product_id", "");
                        }}
                      >
                        <option value="all">{t("studio.catalog.all_categories")}</option>
                        <option value="general">{t("studio.catalog.general")}</option>
                        {catalogCategories.map((category) => (
                          <option key={category.id} value={String(category.id)}>
                            {category.name}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  ) : null}
                  {addItemForm.values.item_type === "service" ? (
                    <Col md={12} className="mb-3">
                      <Label htmlFor="item-service">{t("studio.orders.fields.service.label")}</Label>
                      <Input
                        id="item-service"
                        name="service_id"
                        type="select"
                        value={addItemForm.values.service_id}
                        onChange={(event) => {
                          addItemForm.handleChange(event);
                          addItemForm.setFieldValue("professional_id", "");
                        }}
                        onBlur={addItemForm.handleBlur}
                        invalid={
                          !!(addItemForm.touched.service_id && addItemForm.errors.service_id)
                        }
                      >
                        <option value="">{t("studio.orders.fields.service.placeholder")}</option>
                        {filteredServices.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name} —{" "}
                            {service.price_is_from
                              ? t("studio.services.price.from", {
                                  price: formatMoney(service.price),
                                })
                              : formatMoney(service.price)}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>{addItemForm.errors.service_id}</FormFeedback>
                      {(() => {
                        const selectedAddService = filteredServices.find(
                          (service) => String(service.id) === addItemForm.values.service_id
                        );
                        if (!selectedAddService?.price_is_from) {
                          return null;
                        }
                        return (
                          <Badge color="soft-warning" className="fw-normal mt-2">
                            {t("orders.items.fields.unitPrice.fromHintShort", {
                              price: formatMoney(selectedAddService.price),
                            })}
                          </Badge>
                        );
                      })()}
                      {loadingServiceCredits ? (
                        <small className="text-muted d-block mt-1">
                          {t("studio.orders.fields.service.creditLoading")}
                        </small>
                      ) : null}
                      {!loadingServiceCredits && clubBenefitPreview?.covered ? (
                        <small className="text-primary d-block mt-1">
                          {clubBenefitPreview.visits_limit != null
                            ? t("studio.orders.fields.service.clubCoveredWithLimit", {
                                plan: clubBenefitPreview.plan_name ?? "",
                                used: clubBenefitPreview.visits_used ?? 0,
                                limit: clubBenefitPreview.visits_limit,
                              })
                            : t("studio.orders.fields.service.clubCovered", {
                                plan: clubBenefitPreview.plan_name ?? "",
                              })}
                        </small>
                      ) : null}
                      {!loadingServiceCredits &&
                      !clubBenefitPreview?.covered &&
                      clubBenefitPreview?.limit_reached ? (
                        <small className="text-warning d-block mt-1">
                          {t("studio.orders.fields.service.clubLimitReached", {
                            plan: clubBenefitPreview.plan_name ?? "",
                            used: clubBenefitPreview.visits_used ?? 0,
                            limit: clubBenefitPreview.visits_limit ?? 0,
                          })}
                        </small>
                      ) : null}
                      {!loadingServiceCredits &&
                      !clubBenefitPreview?.covered &&
                      !clubBenefitPreview?.limit_reached &&
                      serviceLoyaltyFree > 0 ? (
                        <small className="text-success d-block mt-1">
                          {t("studio.orders.fields.service.loyaltyFreeReady", {
                            count: serviceLoyaltyFree,
                          })}
                          {serviceLoyaltyExpiresLabel
                            ? ` ${t("studio.orders.fields.service.loyaltyExpires", {
                                date: serviceLoyaltyExpiresLabel,
                              })}`
                            : ""}
                        </small>
                      ) : null}
                      {!loadingServiceCredits &&
                      !clubBenefitPreview?.covered &&
                      !clubBenefitPreview?.limit_reached &&
                      serviceLoyaltyFree === 0 &&
                      serviceLoyaltyRemaining != null &&
                      serviceLoyaltyRemaining > 0 &&
                      serviceLoyaltyRemaining <= 2 ? (
                        <small className="text-warning d-block mt-1">
                          {t("studio.orders.fields.service.loyaltyAlmost", {
                            count: serviceLoyaltyRemaining,
                          })}
                          {serviceLoyaltyValidityDays != null
                            ? ` ${t("studio.orders.fields.service.loyaltyValiditySoon", {
                                days: serviceLoyaltyValidityDays,
                              })}`
                            : ""}
                        </small>
                      ) : null}
                      {!loadingServiceCredits && protocolCreditHint ? (
                        <Alert color="warning" className="small py-2 mt-2 mb-0">
                          {t("studio.orders.fields.service.protocolCreditUsed", {
                            name: protocolCreditHint.protocol_name,
                            n: protocolCreditHint.session_number,
                          })}
                        </Alert>
                      ) : null}
                      {!loadingServiceCredits &&
                      !clubBenefitPreview?.covered &&
                      !clubBenefitPreview?.limit_reached &&
                      serviceCreditSessions != null &&
                      serviceCreditSessions > 0 ? (
                        <>
                          <small className="text-success d-block mt-1">
                            {serviceLoyaltyFree > 0
                              ? t("studio.orders.fields.service.creditRemainingLoyaltyFirst", {
                                  count: serviceCreditSessions,
                                })
                              : t("studio.orders.fields.service.creditRemaining", {
                                  count: serviceCreditSessions,
                                })}
                          </small>
                          <div className="form-check form-switch mt-2">
                            <Input
                              id="item-use-credit"
                              type="switch"
                              role="switch"
                              checked={addItemForm.values.use_credit}
                              onChange={(event) =>
                                addItemForm.setFieldValue(
                                  "use_credit",
                                  event.target.checked
                                )
                              }
                            />
                            <Label className="form-check-label" htmlFor="item-use-credit">
                              {t("studio.orders.fields.useCredit.label")}
                            </Label>
                          </div>
                          <FormText>
                            {addItemForm.values.use_credit
                              ? t("studio.orders.fields.useCredit.hintOn")
                              : t("studio.orders.fields.useCredit.hintOff")}
                          </FormText>
                          {addItemCreditComparison ? (
                            <div className="small mt-2">
                              <div
                                className={
                                  addItemForm.values.use_credit
                                    ? "text-success"
                                    : "text-muted"
                                }
                              >
                                {t("studio.orders.fields.estimate.withCredit", {
                                  line: formatMoney(
                                    addItemCreditComparison.withCreditLine
                                  ),
                                  total: formatMoney(
                                    addItemCreditComparison.withCreditOrderTotal
                                  ),
                                })}
                              </div>
                              <div
                                className={
                                  !addItemForm.values.use_credit
                                    ? "fw-medium"
                                    : "text-muted"
                                }
                              >
                                {t("studio.orders.fields.estimate.withoutCredit", {
                                  line: formatMoney(
                                    addItemCreditComparison.withoutCreditLine
                                  ),
                                  total: formatMoney(
                                    addItemCreditComparison.withoutCreditOrderTotal
                                  ),
                                })}
                              </div>
                            </div>
                          ) : addItemLineEstimateCents != null &&
                            addItemEstimatedOrderTotalCents != null ? (
                            <small className="text-muted d-block mt-2">
                              {t("studio.orders.fields.estimate.line", {
                                amount: formatMoney(addItemLineEstimateCents),
                              })}{" "}
                              ·{" "}
                              {t("studio.orders.fields.estimate.orderTotal", {
                                amount: formatMoney(addItemEstimatedOrderTotalCents),
                              })}
                            </small>
                          ) : null}
                        </>
                      ) : addItemLineEstimateCents != null &&
                        addItemEstimatedOrderTotalCents != null ? (
                        <small className="text-muted d-block mt-2">
                          {t("studio.orders.fields.estimate.line", {
                            amount: formatMoney(addItemLineEstimateCents),
                          })}{" "}
                          ·{" "}
                          {t("studio.orders.fields.estimate.orderTotal", {
                            amount: formatMoney(addItemEstimatedOrderTotalCents),
                          })}
                        </small>
                      ) : null}
                    </Col>
                  ) : addItemForm.values.item_type === "retail" ? (
                    <>
                      <Col md={12} className="mb-3">
                        <Label htmlFor="item-brand-filter">
                          {t("studio.orders.filter.brand")}
                        </Label>
                        <Input
                          id="item-brand-filter"
                          type="search"
                          value={addItemBrandFilter}
                          onChange={(event) => {
                            setAddItemBrandFilter(event.target.value);
                            addItemForm.setFieldValue("product_id", "");
                          }}
                          placeholder={t("studio.orders.filter.brandPlaceholder")}
                        />
                      </Col>
                      <Col md={12} className="mb-3">
                        <Label htmlFor="item-product">{t("studio.orders.fields.product.label")}</Label>
                        <Input
                          id="item-product"
                          name="product_id"
                          type="select"
                          value={addItemForm.values.product_id}
                          onChange={addItemForm.handleChange}
                          onBlur={addItemForm.handleBlur}
                          invalid={
                            !!(addItemForm.touched.product_id && addItemForm.errors.product_id)
                          }
                        >
                          <option value="">{t("studio.orders.fields.product.placeholder")}</option>
                          {filteredProducts.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name}
                              {product.sale_price !== null
                                ? ` — ${formatMoney(product.sale_price)}`
                                : ""}
                            </option>
                          ))}
                        </Input>
                        <FormFeedback>{addItemForm.errors.product_id}</FormFeedback>
                      </Col>
                    </>
                  ) : (
                    <Col md={12} className="mb-3">
                      <Label htmlFor="item-package">
                        {t("studio.orders.fields.package.label")}
                      </Label>
                      <Input
                        id="item-package"
                        name="service_package_id"
                        type="select"
                        value={addItemForm.values.service_package_id}
                        onChange={addItemForm.handleChange}
                        onBlur={addItemForm.handleBlur}
                        invalid={
                          !!(
                            addItemForm.touched.service_package_id &&
                            addItemForm.errors.service_package_id
                          )
                        }
                      >
                        <option value="">
                          {t("studio.orders.fields.package.placeholder")}
                        </option>
                        {servicePackages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name} — {pkg.sessions_count}{" "}
                            {t("studio.orders.fields.package.sessionsSuffix")} —{" "}
                            {formatMoney(pkg.price)}
                          </option>
                        ))}
                      </Input>
                      <FormFeedback>
                        {addItemForm.errors.service_package_id}
                      </FormFeedback>
                      {servicePackages.length === 0 ? (
                        <small className="text-muted d-block mt-1">
                          {t("studio.orders.fields.package.empty")}{" "}
                          <Link to="/service-packages">
                            {t("studio.orders.fields.package.emptyCta")}
                          </Link>
                        </small>
                      ) : null}
                    </Col>
                  )}
                  {addItemForm.values.item_type !== "package" ? (
                  <Col md={12} className="mb-3">
                    <Label htmlFor="item-professional">
                      {useCommissionTracking
                        ? t("studio.orders.fields.professional.label")
                        : t("studio.orders.fields.professional.labelSimple")}
                    </Label>
                    {loadingServiceEnablement &&
                    addItemForm.values.item_type === "service" &&
                    addItemForm.values.service_id ? (
                      <div className="py-2">
                        <Spinner size="sm" color="primary" className="me-2" />
                        {t("studio.orders.enablement.loading")}
                      </div>
                    ) : (
                      <Input
                        id="item-professional"
                        name="professional_id"
                        type="select"
                        value={addItemForm.values.professional_id}
                        onChange={addItemForm.handleChange}
                        onBlur={addItemForm.handleBlur}
                        disabled={
                          addItemForm.values.item_type === "service" &&
                          restrictProfessionalServices &&
                          !addItemForm.values.service_id
                        }
                      >
                        <option value="">{t("studio.orders.fields.professional.none")}</option>
                        {visibleProfessionals.map((professional) => (
                          <option key={professional.id} value={professional.id}>
                            {professional.name}
                          </option>
                        ))}
                      </Input>
                    )}
                    {serviceEnablement?.restrict_mode &&
                      addItemForm.values.item_type === "service" &&
                      addItemForm.values.service_id &&
                      !loadingServiceEnablement &&
                      visibleProfessionals.length === 0 && (
                        <Alert color="warning" className="mt-2 mb-0 py-2">
                          <small>{t("studio.orders.enablement.noProfessionals")}</small>
                        </Alert>
                      )}
                  </Col>
                  ) : null}
                  {addItemForm.values.item_type === "service" && (
                    <Col md={12} className="mb-3">
                      <Label htmlFor="item-execution-notes">
                        {t("orders.items.fields.executionNotes.label")}
                      </Label>
                      <Input
                        id="item-execution-notes"
                        name="execution_notes"
                        type="textarea"
                        rows={3}
                        value={addItemForm.values.execution_notes}
                        onChange={addItemForm.handleChange}
                        onBlur={addItemForm.handleBlur}
                        placeholder={t("orders.items.fields.executionNotes.placeholder")}
                        invalid={
                          !!(
                            addItemForm.touched.execution_notes &&
                            addItemForm.errors.execution_notes
                          )
                        }
                      />
                      <FormFeedback>{addItemForm.errors.execution_notes}</FormFeedback>
                    </Col>
                  )}
                </Row>
                <div className="text-end">
                  <Button color="primary" type="submit" disabled={submitting}>
                    {t("studio.orders.actions.addItem")}
                  </Button>
                </div>
              </Form>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeDetail} disabled={submitting}>
            {t("studio.orders.actions.cancel")}
          </Button>
          {canVoidSelectedOrder && (
            <Button
              color="danger"
              outline
              disabled={submitting || detailLoading || !selectedOrder}
              onClick={() => {
                void handleVoidOrder();
              }}
            >
              {t("studio.orders.void.action")}
            </Button>
          )}
          {(canVoidSelectedOrder || selectedOrder?.status === "paid") && (
            <HelpArticleLink
              articlePath={HELP_ARTICLE_ORDERS_SPLIT_VOID}
              label={t("studio.orders.void.helpLink")}
              className="align-self-center"
            />
          )}
          {!isOrderReadOnly && (
            <>
              {canApplyOrderDiscount ? (
                <Button
                  color="secondary"
                  outline
                  disabled={submitting || detailLoading || !selectedOrder}
                  onClick={() => setDiscountModalOpen(true)}
                >
                  {(selectedOrder?.discount_total_cents ?? 0) > 0
                    ? t("studio.orders.discount.edit")
                    : t("studio.orders.discount.apply")}
                </Button>
              ) : userHasAnyRole(["receptionist"]) ? (
                <Button
                  color="secondary"
                  outline
                  disabled
                  title={t("studio.orders.discount.forbiddenReceptionist")}
                >
                  {t("studio.orders.discount.apply")}
                </Button>
              ) : null}
              {userHasAnyRole(["owner", "manager", "receptionist"]) ? (
                <HelpArticleLink
                  articlePath={HELP_ARTICLE_ORDERS}
                  label={t("studio.orders.discount.helpLink")}
                  className="align-self-center"
                />
              ) : null}
              {(selectedOrder?.discount_total_cents ?? 0) > 0 &&
              userHasAnyRole(["owner", "manager"]) &&
              selectedOrder?.discounts?.[0] ? (
                <Button
                  color="link"
                  className="text-danger"
                  disabled={submitting}
                  onClick={() => {
                    void (async () => {
                      if (!selectedOrder?.discounts?.[0]) {
                        return;
                      }

                      const confirmation = await Swal.fire({
                        title: t("studio.orders.discount.removeConfirmTitle"),
                        text: t("studio.orders.discount.removeConfirmBody"),
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: t(
                          "studio.orders.discount.removeConfirmAction"
                        ),
                        cancelButtonText: t("studio.orders.actions.cancel"),
                        confirmButtonColor: "#d63939",
                      });

                      if (!confirmation.isConfirmed) {
                        return;
                      }

                      try {
                        await removeDiscount(
                          selectedOrder.id,
                          selectedOrder.discounts[0].id
                        );
                        toast.success(t("studio.orders.discount.toast.removeSuccess"));
                        await refreshOrderDetail(selectedOrder.id);
                      } catch (error: any) {
                        toast.error(
                          error?.data || t("studio.orders.discount.toast.removeError")
                        );
                      }
                    })();
                  }}
                >
                  {t("studio.orders.discount.remove")}
                </Button>
              ) : null}
            </>
          )}
          {!isOrderReadOnly && (
          <Button
            color="success"
            disabled={submitting || detailLoading || loadingSettings || !selectedOrder}
            onClick={handleFinalizeOrder}
          >
            {loadingSettings
              ? t("studio.orders.loading_settings")
              : t("studio.orders.actions.checkout")}
          </Button>
          )}
        </ModalFooter>
      </Modal>

      {selectedOrder && (
        <CheckoutConsumptionModal
          isOpen={consumptionPromptOpen}
          toggle={closeCheckoutPrompt}
          orderTotal={selectedOrder.total_amount}
          formatMoney={formatMoney}
          internalProducts={internalProducts}
          loadingProducts={loadingInternalProducts}
          consumptionLines={consumptionLines}
          draftProductId={draftProductId}
          draftQuantity={draftQuantity}
          draftError={consumptionDraftError}
          submitting={submitting}
          onDraftProductChange={(value) => {
            setDraftProductId(value);
            setConsumptionDraftError(null);
          }}
          onDraftQuantityChange={setDraftQuantity}
          onAddLine={handleAddConsumptionLine}
          onRemoveLine={handleRemoveConsumptionLine}
          onSkip={() => {
            void openPaymentModal([]);
          }}
          onConfirm={() => {
            void openPaymentModal(mapConsumptionLinesToPayload());
          }}
        />
      )}

      <CheckoutPaymentModal
        isOpen={paymentModalOpen}
        toggle={closePaymentModal}
        submitting={submitting}
        tenantId={tenantSettings.tenant_id}
        totalAmountCents={selectedOrder?.total_amount ?? 0}
        subtotalAmountCents={selectedOrder?.subtotal_amount}
        discountTotalCents={selectedOrder?.discount_total_cents ?? 0}
        onConfirm={handlePaymentConfirm}
      />

      {selectedOrder ? (
        <ApplyDiscountModal
          isOpen={discountModalOpen}
          toggle={() => setDiscountModalOpen(false)}
          orderId={selectedOrder.id}
          subtotalCents={
            selectedOrder.subtotal_amount ?? selectedOrder.total_amount
          }
          maxManualDiscountBps={discountCapBps}
          showSettingsLink={userHasAnyRole(["owner", "manager"])}
          initialDiscount={
            selectedOrder.discounts?.[0]
              ? {
                  percentBps: selectedOrder.discounts[0].discount_percent_bps,
                  amountCents: selectedOrder.discounts[0].discount_amount_cents,
                  reason: selectedOrder.discounts[0].reason,
                }
              : null
          }
          onApplied={() => {
            toast.success(t("studio.orders.discount.toast.applySuccess"));
            void refreshOrderDetail(selectedOrder.id);
          }}
        />
      ) : null}

      <PixPaymentModal
        isOpen={pixModalOpen}
        onClose={closePixModal}
        orderId={pixOrderId}
        qrCodeBase64={pixQrCodeBase64}
        copyPasteString={pixCopyPasteString}
        onPaymentConfirmed={() => {
          void handlePixPaymentConfirmed();
        }}
      />

      <TerminalPaymentModal
        isOpen={terminalModalOpen}
        onClose={closeTerminalModal}
        orderId={terminalOrderId}
        cardType={terminalCardType}
        onPaymentConfirmed={() => {
          void handleTerminalPaymentConfirmed();
        }}
        onCancelled={() => {
          void handleTerminalPaymentCancelled();
        }}
      />

      <Modal
        isOpen={editingItem !== null}
        toggle={handleCloseEditNotes}
        centered
      >
        <ModalHeader toggle={handleCloseEditNotes}>
          {t("orders.items.fields.executionNotes.label")}
        </ModalHeader>
        <ModalBody>
          {editingItem ? (
            <div className="mb-0">
              <Label htmlFor="edit-execution-notes">
                {t("orders.items.fields.executionNotes.label")}
              </Label>
              <Input
                id="edit-execution-notes"
                type="textarea"
                rows={4}
                value={editNotesValue}
                onChange={(event) => setEditNotesValue(event.target.value)}
                placeholder={t("orders.items.fields.executionNotes.placeholder")}
                maxLength={2000}
              />
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleCloseEditNotes} disabled={submitting}>
            {t("studio.orders.actions.cancel")}
          </Button>
          <Button color="primary" onClick={handleSaveEditNotes} disabled={submitting}>
            {t("orders.items.actions.saveNotes")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={openOrderExistsAlert !== null}
        toggle={() => setOpenOrderExistsAlert(null)}
        centered
      >
        <ModalHeader toggle={() => setOpenOrderExistsAlert(null)}>
          {openOrderExistsAlert?.withPackage
            ? t("studio.orders.alerts.openExistsWithPackageTitle")
            : t("orders.alerts.openExists")}
        </ModalHeader>
        <ModalBody>{openOrderExistsAlert?.message}</ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setOpenOrderExistsAlert(null)}>
            {t("studio.orders.actions.cancel")}
          </Button>
          <Button
            color="primary"
            onClick={() => {
              if (openOrderExistsAlert) {
                void handleGoToOpenOrder(openOrderExistsAlert.orderId);
              }
            }}
          >
            {openOrderExistsAlert?.withPackage
              ? t("studio.orders.actions.addPackageToOpenOrder")
              : t("orders.actions.goToOpenOrder")}
          </Button>
        </ModalFooter>
      </Modal>

      {selectedOrder?.client_id ? (
        <ClientAnamnesisOffcanvas
          isOpen={anamnesisOpen}
          onClose={() => {
            setAnamnesisOpen(false);
            setAnamnesisServiceId(null);
          }}
          clientId={selectedOrder.client_id}
          clientName={selectedOrder.client?.name ?? ""}
          serviceId={anamnesisServiceId}
          onRecordSaved={() => {
            void refreshOrderDetail(selectedOrder.id);
          }}
        />
      ) : null}
    </div>
  );
};

export default OrdersList;
